import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { parsearMensajeError } from './parsear-error';
import {
  Division,
  IConfigDispositivoNSP4G,
  IConfigDispositivoNUC4G,
  ICoordenadas,
  ICorrectora,
  IDispositivo,
  IDispositivoExternoNuc,
  IFilter,
  IMantenimiento,
  IMedidorResidencial,
  IMedidorResidencialAgua,
  IScada,
  IPuntoMedicion,
  IQueryParam,
  IReporteWRC,
  IUnidadPresion,
  Nivel,
  Rol,
  TipoDispositivo,
  IEstado,
  TipoScada,
  IReporte,
  IReporteTotalizadorBove,
  IReporteHorarioEUW300,
  IReporteML107GH,
  IReporteSML,
  IReporteUWMNB,
  IReporteDiarioEUW300,
  UnidadFlujoAcumulado,
} from 'modelos/src';
import { LoginService } from '../modulos/login/login.service';
import type { DivisionVistaPersonalizada } from '../modulos/apn/clientes.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
// Valores (no tipos): el helper de períodos es un archivo hoja de modelos sin
// imports, así que se toma por path directo, como METADATA_ENTIDADES_VINCULABLES.
import {
  esDiaCierreValido,
  periodoDeFecha,
  periodosFacturacion,
} from 'modelos/src/interfaces/tenant/periodo-facturacion';
import { IRangePreset } from './datetime-range-picker/component';
import { IRowIcon } from './tabla/tabla.component';
import { IconosEstadoService } from './iconos-estado.service';
import { api } from '../../environments/environment';
import { nombreDivisionDisplay } from './nombre-division.pipe';

/**
 * Nota sobre "SML" vs `tipoDispositivo: 'WRC'`:
 * En la UI los dispositivos WRC y SML se unifican y se muestran como "SML"
 * porque funcionalmente son el mismo medidor residencial (la única diferencia
 * técnica es el transporte: WRC reporta por 4G/NB-IoT, SML por LoRa).
 * El backend, modelos compartidos y Mongo siguen usando el discriminador
 * 'WRC' y el shape `IReporteWRC`. El renombre completo (modelos + APIs +
 * migración Mongo) está pendiente. Por ahora el front mappea cosméticamente:
 * comparaciones contra 'WRC' se mantienen literales, el label visible pasa
 * por `formatearTipoDispositivo()` (WRC → "SML"), y en los filtros por tipo
 * seleccionar "SML" matchea tanto 'SML' como 'WRC' (ver expandirTipoSml()).
 */

export enum batSemaforo {
  verde = 'green',
  amarillo = 'yellow',
  rojo = 'red',
  error = 'error',
}

@Injectable({
  providedIn: 'root',
})
export class HelperService {
  public regTel = /^(?!0|15)\d{10}$/;

  public isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(
      map((result) => result.matches),
      shareReplay(),
    );

  public pageSize(name: string) {
    if (name) {
      const pageName = `page-${name}`;
      const saved = localStorage.getItem(pageName);
      if (saved) {
        return +saved;
      }
    }
    return 10;
  }
  public pageSizeOptions = [5, 10, 15, 25, 50, 100];

  static esModoOscuro(): boolean {
    return localStorage.getItem('temaOscuro') === 'true';
  }

  static filtroToObject<T>(filtro?: string): IFilter<T> {
    let filter: IFilter<T>;
    try {
      filter = JSON.parse(filtro || '{}');
    } catch (error) {
      filter = {} as any;
    }
    return filter;
  }

  static isDefined(value: any): boolean {
    return value !== undefined && value !== null;
  }

  constructor(
    public readonly snackBar: MatSnackBar,
    private breakpointObserver: BreakpointObserver,
    private iconosEstado: IconosEstadoService,
  ) {}

  public pageEvent($event: any): void {
    localStorage.setItem('pageSize', $event.pageSize);
    this.pageSize = $event.pageSize;
  }

  public static getQueryParams(queryParams?: IQueryParam) {
    let params = new HttpParams();
    if (queryParams) {
      const keysIgnorar = [
        'page',
        'limit',
        'sort',
        'populate',
        'select',
        'filter',
      ];
      if (queryParams?.page || queryParams?.page === 0) {
        params = params.set('page', queryParams.page.toString());
      }
      if (queryParams?.limit || queryParams?.limit === 0) {
        params = params.set('limit', queryParams.limit.toString());
      }
      if (queryParams?.sort) {
        params = params.set('sort', queryParams.sort);
      }
      if (queryParams?.populate) {
        params = params.set('populate', queryParams.populate);
      }
      if (queryParams?.select) {
        params = params.set('select', queryParams.select);
      }
      if (queryParams?.filter) {
        params = params.set('filter', queryParams.filter);
      }

      for (const key in queryParams) {
        if (!keysIgnorar.includes(key)) {
          params = params.set(key, queryParams[key]);
        }
      }
    }
    return params;
  }

  //////////////////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////// Usuarios ////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////////////////

  // Roles Nivel Global

  public esAdminGlobal() {
    const user = LoginService.getUsuario();
    const permiso = user.permisos?.find(
      (x) => x.nivel === 'Global' && x.rol === 'Administrador',
    );
    return !!permiso;
  }

  public esUserAdmin() {
    const user = LoginService.getUsuario();
    const permiso = user.permisos?.find((x) => x.rol === 'Administrador');
    return !!permiso;
  }
  static nivelMasAlto(): Nivel {
    const user = LoginService.getUsuario();
    const global = user.permisos?.find((x) => x.nivel === 'Global');
    if (global) {
      return 'Global';
    }
    const unidadNegocio = user.permisos?.find(
      (x) => x.nivel === 'Unidad de Negocio',
    );
    if (unidadNegocio) {
      return 'Unidad de Negocio';
    }
    return 'Centro Operativo';
  }

  // Nombre visual de la división (no cambia el valor real). Ej: "Residencial"
  // se muestra como "Residencial Gas".
  public nombreDivision(division?: string | null): string {
    return nombreDivisionDisplay(division);
  }

  static nivelMasAltoPorDivision(division: Division): Nivel {
    const user = LoginService.getUsuario();
    const global = user.permisos?.find((x) => x.nivel === 'Global');
    if (global) {
      return 'Global';
    }
    const unidadNegocio = user.permisos?.find(
      (x) => x.division === division && x.nivel === 'Unidad de Negocio',
    );
    if (unidadNegocio) {
      return 'Unidad de Negocio';
    }
    return 'Centro Operativo';
  }

  static rolesGlobales(roles: Rol[]) {
    const user = LoginService.getUsuario();
    const permiso = user.permisos?.find(
      (x) => x.nivel === 'Global' && roles.includes(x.rol!),
    );
    return !!permiso;
  }

  static tieneCuencaPorRolCroma(idCuenca: string) {
    const user = LoginService.getUsuario();
    const permiso = user.permisos?.find(
      (x) => x.rol === 'Croma' && x.idsCuencas?.includes(idCuenca),
    );
    return !!permiso;
  }

  static cuencasPorRolCroma() {
    const user = LoginService.getUsuario();
    const permisos = user.permisos?.filter((x) => x.rol === 'Croma');
    return permisos?.map((x) => x.idsCuencas).flat();
  }

  // Rol por division y nivel
  static rolesPorDivisionYNivel(
    roles: Rol[],
    division: Division,
    nivel: Nivel,
    id: string,
  ) {
    const user = LoginService.getUsuario();
    const permiso = user.permisos?.find((x) => {
      switch (nivel) {
        case 'Unidad de Negocio': {
          return (
            roles.includes(x.rol!) &&
            x.division === division &&
            x.nivel === nivel &&
            x.idsUnidadNegocios?.includes(id)
          );
        }
        case 'Centro Operativo': {
          return (
            roles.includes(x.rol!) &&
            x.division === division &&
            x.nivel === nivel &&
            x.idsCentroOperativos?.includes(id)
          );
        }
        case 'Localidad': {
          return (
            roles.includes(x.rol!) &&
            x.division === division &&
            x.nivel === nivel &&
            x.idsLocalidades?.includes(id)
          );
        }
        case 'Agrupación': {
          return (
            roles.includes(x.rol!) &&
            x.division === division &&
            x.nivel === nivel &&
            x.idsAgrupaciones?.includes(id)
          );
        }
        case 'Global': {
          return (
            roles.includes(x.rol!) &&
            x.division === division &&
            x.nivel === nivel
          );
        }
      }
    });
    return !!permiso;
  }

  // Roles Nivel Unidad de Negocio
  static adminGlobal() {
    const user = LoginService.getUsuario();
    const permiso = user.permisos?.find(
      (x) => x.nivel === 'Global' && x.rol === 'Administrador',
    );
    return !!permiso;
  }

  static rolesEnCualquierUnidadNegocio(roles: Rol[]) {
    const user = LoginService.getUsuario();
    const permiso = user.permisos?.find(
      (x) => x.nivel === 'Unidad de Negocio' && roles.includes(x.rol!),
    );
    return !!permiso;
  }
  static rolesPorUnidadNegocio(roles: Rol[], idUnidadNegocio: string) {
    const user = LoginService.getUsuario();
    const permiso = user.permisos?.find(
      (x) =>
        x.nivel === 'Unidad de Negocio' &&
        roles.includes(x.rol!) &&
        x.idsUnidadNegocios?.includes(idUnidadNegocio),
    );
    return !!permiso;
  }

  // Roles Nivel Operativo
  static rolesEnCualquierCentroOperativo(roles: Rol[]) {
    const user = LoginService.getUsuario();
    const permiso = user.permisos?.find(
      (x) => x.nivel === 'Centro Operativo' && roles.includes(x.rol!),
    );
    return !!permiso;
  }
  static rolesEnCualquierLocalidad(roles: Rol[]) {
    const user = LoginService.getUsuario();
    const permiso = user.permisos?.find(
      (x) => x.nivel === 'Localidad' && roles.includes(x.rol!),
    );
    return !!permiso;
  }
  static rolesPorCentroOperativo(roles: Rol[], idCentroOperativo: string) {
    const user = LoginService.getUsuario();
    const permiso = user.permisos?.find(
      (x) =>
        x.nivel === 'Centro Operativo' &&
        roles.includes(x.rol!) &&
        x.idsCentroOperativos?.includes(idCentroOperativo),
    );
    return !!permiso;
  }
  static rolesEnCualquierAgrupacion(roles: Rol[]) {
    const user = LoginService.getUsuario();
    const permiso = user.permisos?.find(
      (x) => x.nivel === 'Agrupación' && roles.includes(x.rol!),
    );
    return !!permiso;
  }

  // Roles Nivel Unidad negocio y Division
  static rolesEnCualquierUnidadNegocioYDivision(
    roles: Rol[],
    divisiones: Division[],
  ) {
    const user = LoginService.getUsuario();
    const permiso = user.permisos?.find(
      (x) =>
        x.nivel === 'Unidad de Negocio' &&
        roles.includes(x.rol!) &&
        divisiones.includes(x.division!),
    );
    return !!permiso;
  }
  static rolesPorUnidadNegocioYDivision(
    roles: Rol[],
    idUnidadNegocio: string,
    divisiones: Division[],
  ) {
    const user = LoginService.getUsuario();
    const permiso = user.permisos?.find(
      (x) =>
        x.nivel === 'Unidad de Negocio' &&
        roles.includes(x.rol!) &&
        x.idsUnidadNegocios?.includes(idUnidadNegocio) &&
        divisiones.includes(x.division!),
    );
    return !!permiso;
  }

  // Roles Nivel Operativo y Division
  static rolesEnCualquierCentroOperativoYDivision(
    roles: Rol[],
    divisiones: Division[],
  ) {
    const user = LoginService.getUsuario();
    const permiso = user.permisos?.find(
      (x) =>
        x.nivel === 'Centro Operativo' &&
        roles.includes(x.rol!) &&
        divisiones.includes(x.division!),
    );
    return !!permiso;
  }
  static rolesPorCentroOperativoYDivision(
    roles: Rol[],
    idCentroOperativo: string,
    divisiones: Division[],
  ) {
    const user = LoginService.getUsuario();
    const permiso = user.permisos?.find(
      (x) =>
        x.nivel === 'Centro Operativo' &&
        roles.includes(x.rol!) &&
        x.idsCentroOperativos?.includes(idCentroOperativo) &&
        divisiones.includes(x.division!),
    );
    return !!permiso;
  }

  static permisosUNyCO() {
    const user = LoginService.getUsuario();

    if (!user.permisos) {
      return [];
    }

    // si el usuario tiene permisos globales de alguna division retornar esa division y que es global
    const permisoGlobal = user.permisos.find(
      (permiso) =>
        permiso.nivel === 'Global' &&
        permiso.rol &&
        ['Administrador', 'Usuario', 'Visualizar'].includes(permiso.rol),
    );
    if (permisoGlobal) {
      return 'Global';
    }

    // Filtrar permisos de nivel "Unidad de Negocio" y "Centro Operativo"
    const permisosUNyCO = user.permisos.filter(
      (permiso) =>
        (permiso.nivel === 'Unidad de Negocio' ||
          permiso.nivel === 'Centro Operativo') &&
        permiso.rol &&
        ['Administrador', 'Usuario', 'Visualizar'].includes(permiso.rol) &&
        ((permiso.nivel === 'Unidad de Negocio' &&
          permiso.idsUnidadNegocios &&
          permiso.idsUnidadNegocios.length > 0) ||
          (permiso.nivel === 'Centro Operativo' &&
            permiso.idsCentroOperativos &&
            permiso.idsCentroOperativos.length > 0)),
    );

    return permisosUNyCO.map((permiso) => ({
      nivel: permiso.nivel,
      division: permiso.division,
      idsCentroOperativos: permiso.idsCentroOperativos || [],
      rol: permiso.rol,
      // También incluir otros campos si los necesitas
      idsUnidadNegocios: permiso.idsUnidadNegocios || [],
      idsLocalidades: permiso.idsLocalidades || [],
      idsAgrupaciones: permiso.idsAgrupaciones || [],
    }));
  }

  // Roles Nivel localidad y Division
  static rolesEnCualquierLocalidadYDivision(
    roles: Rol[],
    divisiones: Division[],
  ) {
    const user = LoginService.getUsuario();
    const permiso = user.permisos?.find(
      (x) =>
        x.nivel === 'Localidad' &&
        roles.includes(x.rol!) &&
        divisiones.includes(x.division!),
    );
    return !!permiso;
  }

  // Permisos por entidades
  public puedeVerCorrectoras() {
    const user = LoginService.getUsuario();
    // Tipo de dispositivo por Cliente
    if (user.cliente?.tiposDispositivo?.includes('NUC')) {
      // Roles Globales
      if (
        HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
      ) {
        return true;
      }
      // Roles por unidad de negocio
      if (
        HelperService.rolesEnCualquierUnidadNegocioYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Correctoras'],
        )
      ) {
        return true;
      }
      // Roles por centro operativo
      if (
        HelperService.rolesEnCualquierCentroOperativoYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Correctoras'],
        )
      ) {
        return true;
      }
      // Roles por localidad
      if (
        HelperService.rolesEnCualquierLocalidadYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Correctoras'],
        )
      ) {
        return true;
      }
    }
    return false;
  }

  public puedeVerDispositivoExternoNuc() {
    const user = LoginService.getUsuario();
    if (user.cliente?.tiposDispositivo?.includes('NUC')) {
      if (
        HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
      ) {
        return true;
      }
      if (
        HelperService.rolesEnCualquierUnidadNegocioYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Dispositivo Externo NUC'],
        )
      ) {
        return true;
      }
      if (
        HelperService.rolesEnCualquierCentroOperativoYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Dispositivo Externo NUC'],
        )
      ) {
        return true;
      }
      if (
        HelperService.rolesEnCualquierLocalidadYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Dispositivo Externo NUC'],
        )
      ) {
        return true;
      }
    }
    return false;
  }

  public puedeEditarCorrectora(data?: ICorrectora) {
    if (!data) {
      return false;
    }

    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador', 'Usuario'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      data.idUnidadNegocio &&
      HelperService.rolesPorUnidadNegocioYDivision(
        ['Administrador', 'Usuario'],
        data.idUnidadNegocio,
        ['Correctoras'],
      )
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      data.idCentroOperativo &&
      HelperService.rolesPorCentroOperativoYDivision(
        ['Administrador', 'Usuario'],
        data.idCentroOperativo,
        ['Correctoras'],
      )
    ) {
      return true;
    }
    return false;
  }
  public puedeEliminarCorrectora(data: ICorrectora) {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      data.idUnidadNegocio &&
      HelperService.rolesPorUnidadNegocioYDivision(
        ['Administrador'],
        data.idUnidadNegocio,
        ['Correctoras'],
      )
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      data.idCentroOperativo &&
      HelperService.rolesPorCentroOperativoYDivision(
        ['Administrador'],
        data.idCentroOperativo,
        ['Correctoras'],
      )
    ) {
      return true;
    }
    return false;
  }
  public puedeCrearCorrectora() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador', 'Usuario'])) {
      return true;
    }
    // Roles por unidad de negocio
    if (
      HelperService.rolesEnCualquierUnidadNegocioYDivision(
        ['Administrador', 'Usuario'],
        ['Correctoras'],
      )
    ) {
      return true;
    }
    // Roles por centro operativo
    if (
      HelperService.rolesEnCualquierCentroOperativoYDivision(
        ['Administrador', 'Usuario'],
        ['Correctoras'],
      )
    ) {
      return true;
    }
    return false;
  }

  public puedeCrearDispositivoExternoNuc() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador', 'Usuario'])) {
      return true;
    }
    // Roles por unidad de negocio
    if (
      HelperService.rolesEnCualquierUnidadNegocioYDivision(
        ['Administrador', 'Usuario'],
        ['Dispositivo Externo NUC'],
      )
    ) {
      return true;
    }
    // Roles por centro operativo
    if (
      HelperService.rolesEnCualquierCentroOperativoYDivision(
        ['Administrador', 'Usuario'],
        ['Dispositivo Externo NUC'],
      )
    ) {
      return true;
    }
    return false;
  }
  public puedeEditarDispositivoExternoNuc(data?: IDispositivoExternoNuc) {
    if (!data) {
      return false;
    }
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador', 'Usuario'])) {
      return true;
    }
    // Roles por unidad de negocio
    if (
      data.idUnidadNegocio &&
      HelperService.rolesPorUnidadNegocioYDivision(
        ['Administrador', 'Usuario'],
        data.idUnidadNegocio,
        ['Dispositivo Externo NUC'],
      )
    ) {
      return true;
    }
    // Roles por centro operativo
    if (
      data.idCentroOperativo &&
      HelperService.rolesPorCentroOperativoYDivision(
        ['Administrador', 'Usuario'],
        data.idCentroOperativo,
        ['Dispositivo Externo NUC'],
      )
    ) {
      return true;
    }
    return false;
  }
  public puedeEliminarDispositivoExternoNuc(data?: IDispositivoExternoNuc) {
    if (!data) {
      return false;
    }
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }
    // Roles por unidad de negocio
    if (
      data.idUnidadNegocio &&
      HelperService.rolesPorUnidadNegocioYDivision(
        ['Administrador'],
        data.idUnidadNegocio,
        ['Dispositivo Externo NUC'],
      )
    ) {
      return true;
    }
    // Roles por centro operativo
    if (
      data.idCentroOperativo &&
      HelperService.rolesPorCentroOperativoYDivision(
        ['Administrador'],
        data.idCentroOperativo,
        ['Dispositivo Externo NUC'],
      )
    ) {
      return true;
    }
    return false;
  }

  public puedeVerUnidadPresion() {
    const user = LoginService.getUsuario();
    // Tipo de dispositivo por Cliente
    if (user.cliente?.tiposDispositivo?.includes('NSP')) {
      // Roles Globales
      if (
        HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
      ) {
        return true;
      }
      // Roles por unidad de negocio
      if (
        HelperService.rolesEnCualquierUnidadNegocioYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Presión'],
        )
      ) {
        return true;
      }
      // Roles por centro operativo
      if (
        HelperService.rolesEnCualquierCentroOperativoYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Presión'],
        )
      ) {
        return true;
      }
      // Roles por localidad
      if (
        HelperService.rolesEnCualquierLocalidadYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Presión'],
        )
      ) {
        return true;
      }
    }
    return false;
  }
  public puedeEditarUnidadPresion(data?: IUnidadPresion) {
    if (!data) {
      return false;
    }

    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador', 'Usuario'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      data.idUnidadNegocio &&
      HelperService.rolesPorUnidadNegocioYDivision(
        ['Administrador', 'Usuario'],
        data.idUnidadNegocio,
        ['Presión'],
      )
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      data.idCentroOperativo &&
      HelperService.rolesPorCentroOperativoYDivision(
        ['Administrador', 'Usuario'],
        data.idCentroOperativo,
        ['Presión'],
      )
    ) {
      return true;
    }
    return false;
  }
  public puedeEliminarUnidadPresion(data: IUnidadPresion) {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      data.idUnidadNegocio &&
      HelperService.rolesPorUnidadNegocioYDivision(
        ['Administrador'],
        data.idUnidadNegocio,
        ['Presión'],
      )
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      data.idCentroOperativo &&
      HelperService.rolesPorCentroOperativoYDivision(
        ['Administrador'],
        data.idCentroOperativo,
        ['Presión'],
      )
    ) {
      return true;
    }
    return false;
  }
  public puedeCrearUnidadPresion() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador', 'Usuario'])) {
      return true;
    }
    // Roles por unidad de negocio
    if (
      HelperService.rolesEnCualquierUnidadNegocioYDivision(
        ['Administrador', 'Usuario'],
        ['Presión'],
      )
    ) {
      return true;
    }
    // Roles por centro operativo
    if (
      HelperService.rolesEnCualquierCentroOperativoYDivision(
        ['Administrador', 'Usuario'],
        ['Presión'],
      )
    ) {
      return true;
    }
    return false;
  }

  public puedeVerScadaMediciones() {
    const user = LoginService.getUsuario();
    // Tipo de dispositivo por Cliente
    if (user.cliente?.tiposDispositivo?.includes('SCADA Mediciones')) {
      // Roles Globales
      if (
        HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
      ) {
        return true;
      }
      // Roles por unidad de negocio
      if (
        HelperService.rolesEnCualquierUnidadNegocioYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['SCADA Mediciones'],
        )
      ) {
        return true;
      }
      // Roles por centro operativo
      if (
        HelperService.rolesEnCualquierCentroOperativoYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['SCADA Mediciones'],
        )
      ) {
        return true;
      }
      // Roles por localidad
      if (
        HelperService.rolesEnCualquierLocalidadYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['SCADA Mediciones'],
        )
      ) {
        return true;
      }
    }
    return false;
  }
  public puedeEditarScadaMediciones(data?: IScada) {
    if (!data) {
      return false;
    }

    if (data.division !== 'Medición') {
      return false;
    }

    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      data.idUnidadNegocio &&
      HelperService.rolesPorUnidadNegocioYDivision(
        ['Administrador'],
        data.idUnidadNegocio,
        ['SCADA Mediciones'],
      )
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      data.idCentroOperativo &&
      HelperService.rolesPorCentroOperativoYDivision(
        ['Administrador'],
        data.idCentroOperativo,
        ['SCADA Mediciones'],
      )
    ) {
      return true;
    }
    return false;
  }
  public puedeEliminarScadaMediciones(data: IScada) {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      data.idUnidadNegocio &&
      HelperService.rolesPorUnidadNegocioYDivision(
        ['Administrador'],
        data.idUnidadNegocio,
        ['SCADA Mediciones'],
      )
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      data.idCentroOperativo &&
      HelperService.rolesPorCentroOperativoYDivision(
        ['Administrador'],
        data.idCentroOperativo,
        ['SCADA Mediciones'],
      )
    ) {
      return true;
    }
    return false;
  }

  /**
   * Gráficos no es de una división: entra quien ve alguna fuente que se pueda graficar y el selector muestra lo que
   * el listado le devuelve con su alcance. Cada fuente nueva se suma a `DIVISIONES_GRAFICABLES`; el clima y el
   * linepack, con sus módulos.
   */
  public puedeVerGraficos() {
    return (
      this.divisionesGraficables().length > 0 ||
      this.puedeVerClima() ||
      this.puedeVerLinepack()
    );
  }

  /** Las divisiones graficables que el usuario ve. */
  public divisionesGraficables(): Division[] {
    return this.divisionesVisibles().filter((d) =>
      HelperService.DIVISIONES_GRAFICABLES.includes(d),
    );
  }

  public static readonly DIVISIONES_GRAFICABLES: Division[] = [
    'SCADA Unifilares',
    'SCADA Mediciones',
    'Presión',
    'Correctoras',
  ];

  public puedeVerScadaUnifilares() {
    const user = LoginService.getUsuario();
    // Tipo de dispositivo por Cliente
    if (user.cliente?.tiposDispositivo?.includes('SCADA Unifilares')) {
      // Roles Globales
      if (
        HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
      ) {
        return true;
      }
      // Roles por unidad de negocio
      if (
        HelperService.rolesEnCualquierUnidadNegocioYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['SCADA Unifilares'],
        )
      ) {
        return true;
      }
      // Roles por centro operativo
      if (
        HelperService.rolesEnCualquierCentroOperativoYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['SCADA Unifilares'],
        )
      ) {
        return true;
      }
      // Roles por localidad
      if (
        HelperService.rolesEnCualquierLocalidadYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['SCADA Unifilares'],
        )
      ) {
        return true;
      }
    }
    return false;
  }
  public puedeEditarScadaUnifilares(data?: IScada) {
    if (!data) {
      return false;
    }

    if (data.division !== 'Unifilar') {
      return false;
    }

    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      data.idUnidadNegocio &&
      HelperService.rolesPorUnidadNegocioYDivision(
        ['Administrador'],
        data.idUnidadNegocio,
        ['SCADA Unifilares'],
      )
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      data.idCentroOperativo &&
      HelperService.rolesPorCentroOperativoYDivision(
        ['Administrador'],
        data.idCentroOperativo,
        ['SCADA Unifilares'],
      )
    ) {
      return true;
    }
    return false;
  }
  public puedeEliminarScadaUnifilares(data: IScada) {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      data.idUnidadNegocio &&
      HelperService.rolesPorUnidadNegocioYDivision(
        ['Administrador'],
        data.idUnidadNegocio,
        ['SCADA Unifilares'],
      )
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      data.idCentroOperativo &&
      HelperService.rolesPorCentroOperativoYDivision(
        ['Administrador'],
        data.idCentroOperativo,
        ['SCADA Unifilares'],
      )
    ) {
      return true;
    }
    return false;
  }

  public puedeHabilitarScada(data?: IScada) {
    if (!data?.division) {
      return false;
    }
    const division: Division =
      data.division === 'Medición' ? 'SCADA Mediciones' : 'SCADA Unifilares';

    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador', 'Usuario'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      data.idUnidadNegocio &&
      HelperService.rolesPorUnidadNegocioYDivision(
        ['Administrador', 'Usuario'],
        data.idUnidadNegocio,
        [division],
      )
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      data.idCentroOperativo &&
      HelperService.rolesPorCentroOperativoYDivision(
        ['Administrador', 'Usuario'],
        data.idCentroOperativo,
        [division],
      )
    ) {
      return true;
    }
    return false;
  }

  public puedeVerResidencialAgua() {
    const user = LoginService.getUsuario();
    const medidores: TipoDispositivo[] = [
      'ML107A',
      'ML107GH',
      'MRA',
      'EUW300',
      'BOVE',
      'UWM-NB',
    ];
    // Tipo de dispositivo por Cliente
    if (user.cliente?.tiposDispositivo?.some((x) => medidores.includes(x))) {
      // Roles Globales
      if (
        HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
      ) {
        return true;
      }
      // Roles por unidad de negocio
      if (
        HelperService.rolesEnCualquierUnidadNegocioYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Residencial Agua'],
        )
      ) {
        return true;
      }
      // Roles por centro operativo
      if (
        HelperService.rolesEnCualquierCentroOperativoYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Residencial Agua'],
        )
      ) {
        return true;
      }
      // Roles por localidad
      if (
        HelperService.rolesEnCualquierLocalidadYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Residencial Agua'],
        )
      ) {
        return true;
      }
    }
    return false;
  }

  public puedeVerResidencial() {
    const user = LoginService.getUsuario();
    const medidores: TipoDispositivo[] = ['SML', 'WRC'];
    // Tipo de dispositivo por Cliente
    if (user.cliente?.tiposDispositivo?.some((x) => medidores.includes(x))) {
      // Roles Globales
      if (
        HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
      ) {
        return true;
      }
      // Roles por unidad de negocio
      if (
        HelperService.rolesEnCualquierUnidadNegocioYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Residencial'],
        )
      ) {
        return true;
      }
      // Roles por centro operativo
      if (
        HelperService.rolesEnCualquierCentroOperativoYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Residencial'],
        )
      ) {
        return true;
      }
      // Roles por localidad
      if (
        HelperService.rolesEnCualquierLocalidadYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Residencial'],
        )
      ) {
        return true;
      }
    }
    return false;
  }

  // Espeja puedeVerResidencial pero excluye el rol 'Visualizar' (solo lectura):
  // el menú de configuración de Subzonas Tarifarias es de gestión.
  public puedeVerSubzonasTarifarias() {
    const user = LoginService.getUsuario();
    const medidores: TipoDispositivo[] = ['SML', 'WRC'];
    if (user.cliente?.tiposDispositivo?.some((x) => medidores.includes(x))) {
      // Roles Globales
      if (HelperService.rolesGlobales(['Administrador', 'Usuario'])) {
        return true;
      }
      // Roles por unidad de negocio
      if (
        HelperService.rolesEnCualquierUnidadNegocioYDivision(
          ['Administrador', 'Usuario'],
          ['Residencial'],
        )
      ) {
        return true;
      }
      // Roles por centro operativo
      if (
        HelperService.rolesEnCualquierCentroOperativoYDivision(
          ['Administrador', 'Usuario'],
          ['Residencial'],
        )
      ) {
        return true;
      }
      // Roles por localidad
      if (
        HelperService.rolesEnCualquierLocalidadYDivision(
          ['Administrador', 'Usuario'],
          ['Residencial'],
        )
      ) {
        return true;
      }
    }
    return false;
  }

  public puedeVerMedidoresElectricos() {
    const user = LoginService.getUsuario();
    const medidores: TipoDispositivo[] = ['NME'];
    // Tipo de dispositivo por Cliente
    if (user.cliente?.tiposDispositivo?.some((x) => medidores.includes(x))) {
      // Roles Globales
      if (
        HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
      ) {
        return true;
      }
      // Roles por unidad de negocio
      if (
        HelperService.rolesEnCualquierUnidadNegocioYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Medidores Eléctricos'],
        )
      ) {
        return true;
      }
      // Roles por centro operativo
      if (
        HelperService.rolesEnCualquierCentroOperativoYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Medidores Eléctricos'],
        )
      ) {
        return true;
      }
      // Roles por localidad
      if (
        HelperService.rolesEnCualquierLocalidadYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Medidores Eléctricos'],
        )
      ) {
        return true;
      }
    }
    return false;
  }

  public puedeVerEstacionesBombeo() {
    const user = LoginService.getUsuario();
    const rtus: TipoDispositivo[] = ['UC300'];
    // Tipo de dispositivo por Cliente
    if (user.cliente?.tiposDispositivo?.some((x) => rtus.includes(x))) {
      // Roles Globales
      if (
        HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
      ) {
        return true;
      }
      // Roles por unidad de negocio
      if (
        HelperService.rolesEnCualquierUnidadNegocioYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Estaciones de Bombeo'],
        )
      ) {
        return true;
      }
      // Roles por centro operativo
      if (
        HelperService.rolesEnCualquierCentroOperativoYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Estaciones de Bombeo'],
        )
      ) {
        return true;
      }
      // Roles por localidad
      if (
        HelperService.rolesEnCualquierLocalidadYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Estaciones de Bombeo'],
        )
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Alta, edición, baja y vinculación de estaciones de bombeo: gas-api-cliente
   * exige el rol Administrador en esos endpoints.
   */
  public puedeEditarEstacionesBombeo() {
    if (!this.puedeVerEstacionesBombeo()) return false;
    if (HelperService.rolesGlobales(['Administrador'])) return true;
    return (
      HelperService.rolesEnCualquierUnidadNegocioYDivision(
        ['Administrador'],
        ['Estaciones de Bombeo'],
      ) ||
      HelperService.rolesEnCualquierCentroOperativoYDivision(
        ['Administrador'],
        ['Estaciones de Bombeo'],
      )
    );
  }

  public puedeEditarResidencial(data?: IUnidadPresion) {
    if (!data) {
      return false;
    }

    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador', 'Usuario'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      data.idUnidadNegocio &&
      HelperService.rolesPorUnidadNegocioYDivision(
        ['Administrador', 'Usuario'],
        data.idUnidadNegocio,
        ['Residencial'],
      )
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      data.idCentroOperativo &&
      HelperService.rolesPorCentroOperativoYDivision(
        ['Administrador', 'Usuario'],
        data.idCentroOperativo,
        ['Residencial'],
      )
    ) {
      return true;
    }
    return false;
  }
  public puedeEliminarResidencial(data: IUnidadPresion) {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      data.idUnidadNegocio &&
      HelperService.rolesPorUnidadNegocioYDivision(
        ['Administrador'],
        data.idUnidadNegocio,
        ['Residencial'],
      )
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      data.idCentroOperativo &&
      HelperService.rolesPorCentroOperativoYDivision(
        ['Administrador'],
        data.idCentroOperativo,
        ['Residencial'],
      )
    ) {
      return true;
    }
    return false;
  }

  public puedeVerMedidores() {
    const user = LoginService.getUsuario();
    // Tipo de dispositivo por Cliente
    if (user.cliente?.tiposDispositivo?.includes('SML')) {
      // Roles Globales
      if (
        HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
      ) {
        return true;
      }
      // Roles por unidad de negocio
      if (
        HelperService.rolesEnCualquierUnidadNegocioYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Residencial'],
        )
      ) {
        return true;
      }
      // Roles por centro operativo
      if (
        HelperService.rolesEnCualquierCentroOperativoYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Residencial'],
        )
      ) {
        return true;
      }
      // Roles por localidad
      if (
        HelperService.rolesEnCualquierLocalidadYDivision(
          ['Administrador', 'Usuario', 'Visualizar'],
          ['Residencial'],
        )
      ) {
        return true;
      }
    }

    return false;
  }
  public puedeEditarMedidor(data?: IMedidorResidencial) {
    if (!data) return false;
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador', 'Usuario'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      data.idUnidadNegocio &&
      HelperService.rolesPorUnidadNegocio(
        ['Administrador', 'Usuario'],
        data.idUnidadNegocio,
      )
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      data.idCentroOperativo &&
      HelperService.rolesPorCentroOperativo(
        ['Administrador', 'Usuario'],
        data.idCentroOperativo,
      )
    ) {
      return true;
    }
    return false;
  }
  public puedeEliminarMedidor(data?: IMedidorResidencial) {
    if (!data) return false;
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      data.idUnidadNegocio &&
      HelperService.rolesPorUnidadNegocio(
        ['Administrador'],
        data.idUnidadNegocio,
      )
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      data.idCentroOperativo &&
      HelperService.rolesPorCentroOperativo(
        ['Administrador'],
        data.idCentroOperativo,
      )
    ) {
      return true;
    }
    return false;
  }
  public puedeCrearMedidor() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador', 'Usuario'])) {
      return true;
    }
    // Roles por unidad de negocio
    if (
      HelperService.rolesEnCualquierUnidadNegocioYDivision(
        ['Administrador', 'Usuario'],
        ['Residencial'],
      )
    ) {
      return true;
    }
    // Roles por centro operativo
    if (
      HelperService.rolesEnCualquierCentroOperativoYDivision(
        ['Administrador', 'Usuario'],
        ['Residencial'],
      )
    ) {
      return true;
    }
    return false;
  }

  public puedeVerML107A() {
    const user = LoginService.getUsuario();
    // Tipo de dispositivo por Cliente
    if (user.cliente?.tiposDispositivo?.includes('ML107A')) {
      // Roles Globales
      if (
        HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
      ) {
        return true;
      }
      // Roles por unidad de negocio
      if (
        HelperService.rolesEnCualquierUnidadNegocio([
          'Administrador',
          'Usuario',
          'Visualizar',
        ])
      ) {
        return true;
      }
      // Roles por centro operativo
      if (
        HelperService.rolesEnCualquierCentroOperativo([
          'Administrador',
          'Usuario',
          'Visualizar',
        ])
      ) {
        return true;
      }
    }

    return false;
  }

  public puedeVerML107GH() {
    const user = LoginService.getUsuario();
    // Tipo de dispositivo por Cliente
    if (user.cliente?.tiposDispositivo?.includes('ML107GH')) {
      // Roles Globales
      if (
        HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
      ) {
        return true;
      }
      // Roles por unidad de negocio
      if (
        HelperService.rolesEnCualquierUnidadNegocio([
          'Administrador',
          'Usuario',
          'Visualizar',
        ])
      ) {
        return true;
      }
      // Roles por centro operativo
      if (
        HelperService.rolesEnCualquierCentroOperativo([
          'Administrador',
          'Usuario',
          'Visualizar',
        ])
      ) {
        return true;
      }
    }

    return false;
  }

  public puedeVerEUW300() {
    const user = LoginService.getUsuario();
    // Tipo de dispositivo por Cliente
    if (user.cliente?.tiposDispositivo?.includes('EUW300')) {
      // Roles Globales
      if (
        HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
      ) {
        return true;
      }
      // Roles por unidad de negocio
      if (
        HelperService.rolesEnCualquierUnidadNegocio([
          'Administrador',
          'Usuario',
          'Visualizar',
        ])
      ) {
        return true;
      }
      // Roles por centro operativo
      if (
        HelperService.rolesEnCualquierCentroOperativo([
          'Administrador',
          'Usuario',
          'Visualizar',
        ])
      ) {
        return true;
      }
    }

    return false;
  }

  public puedeVerMra() {
    const user = LoginService.getUsuario();
    // Tipo de dispositivo por Cliente
    if (user.cliente?.tiposDispositivo?.includes('MRA')) {
      // Roles Globales
      if (
        HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
      ) {
        return true;
      }
      // Roles por unidad de negocio
      if (
        HelperService.rolesEnCualquierUnidadNegocio([
          'Administrador',
          'Usuario',
          'Visualizar',
        ])
      ) {
        return true;
      }
      // Roles por centro operativo
      if (
        HelperService.rolesEnCualquierCentroOperativo([
          'Administrador',
          'Usuario',
          'Visualizar',
        ])
      ) {
        return true;
      }
    }

    return false;
  }
  public puedeVerUwmNb() {
    const user = LoginService.getUsuario();
    // Tipo de dispositivo por Cliente
    if (user.cliente?.tiposDispositivo?.includes('UWM-NB')) {
      // Roles Globales
      if (
        HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
      ) {
        return true;
      }
      // Roles por unidad de negocio
      if (
        HelperService.rolesEnCualquierUnidadNegocio([
          'Administrador',
          'Usuario',
          'Visualizar',
        ])
      ) {
        return true;
      }
      // Roles por centro operativo
      if (
        HelperService.rolesEnCualquierCentroOperativo([
          'Administrador',
          'Usuario',
          'Visualizar',
        ])
      ) {
        return true;
      }
    }

    return false;
  }
  public puedeEditarMra(data: IMedidorResidencialAgua) {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador', 'Usuario'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      data.idUnidadNegocio &&
      HelperService.rolesPorUnidadNegocio(
        ['Administrador', 'Usuario'],
        data.idUnidadNegocio,
      )
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      data.idCentroOperativo &&
      HelperService.rolesPorCentroOperativo(
        ['Administrador', 'Usuario'],
        data.idCentroOperativo,
      )
    ) {
      return true;
    }
    return false;
  }
  public puedeEliminarMra(data: IMedidorResidencialAgua) {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      data.idUnidadNegocio &&
      HelperService.rolesPorUnidadNegocio(
        ['Administrador'],
        data.idUnidadNegocio,
      )
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      data.idCentroOperativo &&
      HelperService.rolesPorCentroOperativo(
        ['Administrador'],
        data.idCentroOperativo,
      )
    ) {
      return true;
    }
    return false;
  }

  public puedeVerUsuarios() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }

    return false;
  }

  private tieneSoloScada() {
    const user = LoginService.getUsuario();
    const divisiones: Division[] = ['SCADA Mediciones', 'SCADA Unifilares'];
    const permisoNoScada = user.permisos?.find(
      (x) => !divisiones.includes(x.division!),
    );
    if (!permisoNoScada && user.permisos?.length) {
      return true;
    }
    return false;
  }

  /**
   * Divisiones que el usuario ve: cruza sus permisos con los tipos de
   * dispositivo del cliente. Mismo criterio que el listado de puntos, más SCADA.
   */
  public divisionesVisibles(): Division[] {
    const divisiones: Division[] = [];
    if (this.puedeVerCorrectoras()) divisiones.push('Correctoras');
    if (this.puedeVerUnidadPresion()) divisiones.push('Presión');
    if (this.puedeVerResidencial()) divisiones.push('Residencial');
    if (this.puedeVerResidencialAgua()) divisiones.push('Residencial Agua');
    if (this.puedeVerScadaUnifilares()) divisiones.push('SCADA Unifilares');
    if (this.puedeVerScadaMediciones()) divisiones.push('SCADA Mediciones');
    if (this.puedeVerDispositivoExternoNuc())
      divisiones.push('Dispositivo Externo NUC');
    if (this.puedeVerMedidoresElectricos())
      divisiones.push('Medidores Eléctricos');
    if (this.puedeVerEstacionesBombeo())
      divisiones.push('Estaciones de Bombeo');
    return divisiones;
  }

  public puedeVerPuntosDeMedicion() {
    // Roles Globales
    if (
      HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
    ) {
      return true;
    }
    // Roles por unidad de negocio
    if (
      HelperService.rolesEnCualquierUnidadNegocio([
        'Administrador',
        'Usuario',
        'Visualizar',
      ])
    ) {
      return true;
    }
    // Roles por centro operativo
    if (
      HelperService.rolesEnCualquierCentroOperativo([
        'Administrador',
        'Usuario',
        'Visualizar',
      ])
    ) {
      return true;
    }
    // Roles por localidad
    if (
      HelperService.rolesEnCualquierLocalidad([
        'Administrador',
        'Usuario',
        'Visualizar',
      ])
    ) {
      return true;
    }
    // Roles por agrupacion
    if (
      HelperService.rolesEnCualquierAgrupacion([
        'Administrador',
        'Usuario',
        'Visualizar',
      ])
    ) {
      return true;
    }
    return false;
  }
  /**
   * Clasificar un punto es una edición, no una lectura: el backend restringe
   * los PATCH de clasificación a Administrador y Usuario. `Visualizar` entra a
   * la pantalla y ve la cobertura, pero no puede corregir nada.
   *
   * El alcance fino (qué puntos concretos) lo resuelve el backend con el filtro
   * de permisos; acá solo se decide si se muestran los controles de edición.
   */
  public puedeClasificarPuntos() {
    if (HelperService.rolesGlobales(['Administrador', 'Usuario'])) {
      return true;
    }
    if (
      HelperService.rolesEnCualquierUnidadNegocio(['Administrador', 'Usuario'])
    ) {
      return true;
    }
    if (
      HelperService.rolesEnCualquierCentroOperativo([
        'Administrador',
        'Usuario',
      ])
    ) {
      return true;
    }
    if (HelperService.rolesEnCualquierLocalidad(['Administrador', 'Usuario'])) {
      return true;
    }
    return false;
  }

  public puedeCrearPuntosDeMedicion() {
    // Si el usuario tiene solo division scada, no puede crear puntos de medición
    if (this.tieneSoloScada()) {
      return false;
    }

    // Tipo de dispositivo por Cliente
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador', 'Usuario'])) {
      return true;
    }
    // Roles por unidad de negocio
    if (
      HelperService.rolesEnCualquierUnidadNegocio(['Administrador', 'Usuario'])
    ) {
      return true;
    }
    // Roles por centro operativo
    if (
      HelperService.rolesEnCualquierCentroOperativo([
        'Administrador',
        'Usuario',
      ])
    ) {
      return true;
    }
    return false;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public puedeEditarPuntosDeMedicion(data: IPuntoMedicion) {
    const user = LoginService.getUsuario();

    const divisiones: Division[] = [
      'Correctoras',
      'Presión',
      'Residencial',
      'Residencial Agua',
      'Medidores Eléctricos',
      'Estaciones de Bombeo',
      'Dispositivo Externo NUC',
    ];
    if (divisiones.includes(data.division!)) {
      const tipos: TipoDispositivo[] = [
        'NUC',
        'NSP',
        'SML',
        'WRC',
        'VERIBOX MICRO',
        'MRA',
        'ML107A',
        'ML107GH',
        'EUW300',
        'BOVE',
        'NME',
        'UWM-NB',
        'UC300',
      ];
      // Tipo de dispositivo por Cliente
      if (user.cliente?.tiposDispositivo?.some((x) => tipos.includes(x))) {
        // Roles Globales
        if (HelperService.rolesGlobales(['Administrador', 'Usuario'])) {
          return true;
        }
        // Roles por unidad de negocio
        if (
          HelperService.rolesEnCualquierUnidadNegocio([
            'Administrador',
            'Usuario',
          ])
        ) {
          return true;
        }
        // Roles por centro operativo
        if (
          HelperService.rolesEnCualquierCentroOperativo([
            'Administrador',
            'Usuario',
          ])
        ) {
          return true;
        }
        // Roles por localidad
        if (
          HelperService.rolesEnCualquierLocalidad(['Administrador', 'Usuario'])
        ) {
          return true;
        }
      }
    }

    // PUNTO SCADA
    const divisionesScada: Division[] = [
      'SCADA Mediciones',
      'SCADA Unifilares',
    ];
    if (divisionesScada.includes(data.division!)) {
      const tiposSCADA: TipoDispositivo[] = [
        'SCADA Mediciones',
        'SCADA Unifilares',
      ];
      // Tipo de dispositivo por Cliente
      if (user.cliente?.tiposDispositivo?.some((x) => tiposSCADA.includes(x))) {
        // Roles Globales
        if (HelperService.rolesGlobales(['Administrador'])) {
          return true;
        }
        // Roles por unidad de negocio
        if (
          HelperService.rolesPorDivisionYNivel(
            ['Administrador'],
            data.division!,
            'Unidad de Negocio',
            data.idUnidadNegocio!,
          )
        ) {
          return true;
        }
        // Roles por centro operativo
        if (
          HelperService.rolesPorDivisionYNivel(
            ['Administrador'],
            data.division!,
            'Centro Operativo',
            data.idCentroOperativo!,
          )
        ) {
          return true;
        }
      }
    }

    return false;
  }

  public puedeImportarPuntosDeMedicion(division: Division): boolean {
    // Si el usuario tiene solo divisiones SCADA en sus permisos, no puede importar nada
    if (this.tieneSoloScada()) {
      return false;
    }

    // SCADA nunca puede importarse
    const divisionesScada: Division[] = [
      'SCADA Mediciones',
      'SCADA Unifilares',
    ];
    if (divisionesScada.includes(division)) {
      return false;
    }

    const user = LoginService.getUsuario();
    const tipos: TipoDispositivo[] = [
      'NUC',
      'NSP',
      'SML',
      'WRC',
      'VERIBOX MICRO',
      'MRA',
      'ML107A',
      'ML107GH',
      'EUW300',
      'BOVE',
      'UWM-NB',
    ];
    // Tipo de dispositivo por Cliente
    if (user.cliente?.tiposDispositivo?.some((x) => tipos.includes(x))) {
      // Roles Globales
      if (HelperService.rolesGlobales(['Administrador', 'Usuario'])) {
        return true;
      }
      // Roles por unidad de negocio con la división específica
      if (
        HelperService.rolesEnCualquierUnidadNegocioYDivision(
          ['Administrador', 'Usuario'],
          [division],
        )
      ) {
        return true;
      }
      // Roles por centro operativo con la división específica
      if (
        HelperService.rolesEnCualquierCentroOperativoYDivision(
          ['Administrador', 'Usuario'],
          [division],
        )
      ) {
        return true;
      }
      // Roles por localidad con la división específica
      if (
        HelperService.rolesEnCualquierLocalidadYDivision(
          ['Administrador', 'Usuario'],
          [division],
        )
      ) {
        return true;
      }
    }

    return false;
  }

  public puedeEliminarPuntosDeMedicion(data: IPuntoMedicion) {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      data.idUnidadNegocio &&
      HelperService.rolesPorUnidadNegocio(
        ['Administrador'],
        data.idUnidadNegocio,
      )
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      data.idCentroOperativo &&
      HelperService.rolesPorCentroOperativo(
        ['Administrador'],
        data.idCentroOperativo,
      )
    ) {
      return true;
    }
    return false;
  }

  public puedeEditarDispositivos() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador', 'Usuario'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      HelperService.rolesEnCualquierUnidadNegocio(['Administrador', 'Usuario'])
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      HelperService.rolesEnCualquierCentroOperativo([
        'Administrador',
        'Usuario',
      ])
    ) {
      return true;
    }

    return false;
  }

  public puedeConfigurarNSP() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }
    // Roles por unidad de negocio
    if (HelperService.rolesEnCualquierUnidadNegocio(['Administrador'])) {
      return true;
    }
    // Roles por centro operativo
    if (HelperService.rolesEnCualquierCentroOperativo(['Administrador'])) {
      return true;
    }
    return false;
  }

  public puedeConfigurarNUC() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }
    // Roles por unidad de negocio
    if (HelperService.rolesEnCualquierUnidadNegocio(['Administrador'])) {
      return true;
    }
    // Roles por centro operativo
    if (HelperService.rolesEnCualquierCentroOperativo(['Administrador'])) {
      return true;
    }
    return false;
  }

  public puedeConfigurarSML() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }
    // Roles por unidad de negocio
    if (HelperService.rolesEnCualquierUnidadNegocio(['Administrador'])) {
      return true;
    }
    // Roles por centro operativo
    if (HelperService.rolesEnCualquierCentroOperativo(['Administrador'])) {
      return true;
    }
    return false;
  }

  public puedeConfigurarEUW300() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }
    // Roles por unidad de negocio
    if (HelperService.rolesEnCualquierUnidadNegocio(['Administrador'])) {
      return true;
    }
    // Roles por centro operativo
    if (HelperService.rolesEnCualquierCentroOperativo(['Administrador'])) {
      return true;
    }
    return false;
  }

  public puedeConfigurarVeribox(dispositivo: IDispositivo) {
    // Si no reportó nunca
    const ultimoReporte = dispositivo?.fechaUltimaComunicacion;
    if (!ultimoReporte) {
      return false;
    }

    // Si no reportó en los últimos 2 días
    const fechaMinima = new Date();
    fechaMinima.setDate(fechaMinima.getDate() - 2);
    const fechaReporte = new Date(ultimoReporte);
    if (fechaReporte < fechaMinima) {
      return false;
    }

    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }
    // Roles por unidad de negocio
    if (HelperService.rolesEnCualquierUnidadNegocio(['Administrador'])) {
      return true;
    }
    // Roles por centro operativo
    if (HelperService.rolesEnCualquierCentroOperativo(['Administrador'])) {
      return true;
    }
    return false;
  }

  public puedeListarDispositivos() {
    // Roles Globales
    if (
      HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
    ) {
      return true;
    }
    // Roles por unidad de negocio
    if (
      HelperService.rolesEnCualquierUnidadNegocio([
        'Administrador',
        'Usuario',
        'Visualizar',
      ])
    ) {
      return true;
    }
    // Roles por centro operativo
    if (
      HelperService.rolesEnCualquierCentroOperativo([
        'Administrador',
        'Usuario',
        'Visualizar',
      ])
    ) {
      return true;
    }
    return false;
  }
  public puedeVerDispositivos() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador', 'Usuario'])) {
      return true;
    }
    // Roles por unidad de negocio
    if (
      HelperService.rolesEnCualquierUnidadNegocio(['Administrador', 'Usuario'])
    ) {
      return true;
    }
    // Roles por centro operativo
    if (
      HelperService.rolesEnCualquierCentroOperativo([
        'Administrador',
        'Usuario',
      ])
    ) {
      return true;
    }
    return false;
  }

  public puedeVerUnidadesNegocio() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }
    return false;
  }

  public puedeVerAuditorias() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }
    return false;
  }

  public puedeVerAlertas() {
    // Roles Globales
    if (
      HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
    ) {
      return true;
    }
    return false;
  }

  public puedeVerCentrosOperativos() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }
    return false;
  }

  public puedeVerLocalidades() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }
    return false;
  }

  public puedeListarCuencas() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (HelperService.rolesEnCualquierUnidadNegocio(['Administrador'])) {
      return true;
    }

    // Roles por centro operativo
    if (HelperService.rolesEnCualquierCentroOperativo(['Administrador'])) {
      return true;
    }

    return false;
  }
  public puedeVerCuencas() {
    // Roles Globales
    if (
      HelperService.rolesGlobales(['Administrador']) &&
      this.puedeVerCorrectoras()
    ) {
      return true;
    }
    return false;
  }

  public puedeListarGrupos() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (HelperService.rolesEnCualquierUnidadNegocio(['Administrador'])) {
      return true;
    }

    // Roles por centro operativo
    if (HelperService.rolesEnCualquierCentroOperativo(['Administrador'])) {
      return true;
    }

    return false;
  }
  public puedeVerGrupos() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (HelperService.rolesEnCualquierUnidadNegocio(['Administrador'])) {
      return true;
    }

    // Roles por centro operativo
    if (HelperService.rolesEnCualquierCentroOperativo(['Administrador'])) {
      return true;
    }

    return false;
  }

  public puedeVerAgrupaciones() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }

    return false;
  }

  public puedeVerSms() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      HelperService.rolesEnCualquierUnidadNegocioYDivision(
        ['Administrador'],
        ['Presión'],
      )
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      HelperService.rolesEnCualquierCentroOperativoYDivision(
        ['Administrador'],
        ['Presión'],
      )
    ) {
      return true;
    }
    return false;
  }

  public puedeVerCromatografias() {
    // Roles Globales
    if (
      HelperService.rolesGlobales(['Administrador', 'Laboratorista']) &&
      this.puedeVerCorrectoras()
    ) {
      return true;
    }

    // Roles por unidad de negocio
    if (
      HelperService.rolesEnCualquierUnidadNegocioYDivision(
        ['Administrador', 'Croma'],
        ['Correctoras'],
      )
    ) {
      return true;
    }

    // Roles por centro operativo
    if (
      HelperService.rolesEnCualquierCentroOperativoYDivision(
        ['Administrador', 'Croma'],
        ['Correctoras'],
      )
    ) {
      return true;
    }

    // Roles por localidad
    if (
      HelperService.rolesEnCualquierLocalidadYDivision(
        ['Administrador', 'Croma'],
        ['Correctoras'],
      )
    ) {
      return true;
    }

    return false;
  }

  public puedeVerApns() {
    // Roles Globales
    if (
      HelperService.rolesGlobales(['Administrador']) &&
      (this.puedeVerCorrectoras() || this.puedeVerUnidadPresion())
    ) {
      return true;
    }
    return false;
  }

  public puedeVerSincHoraria() {
    // Roles Globales
    if (
      HelperService.rolesGlobales(['Administrador']) &&
      this.puedeVerCorrectoras()
    ) {
      return true;
    }
    return false;
  }

  /**
   * Configuración Global «Parámetros OBIS»: el `reporte_mask` que se le manda a
   * todos los NME del cliente.
   *
   * Mismo patrón que `puedeVerSincHoraria`: feature del cliente (tener NME) +
   * rol global. El rol es 'Administrador' y no menos porque es exactamente lo
   * que exige `PUT /clientes/parametrosObis` en gas-api-cliente. Sin este gate un
   * usuario común veía el tab, elegía las métricas y se comía un 403 al guardar.
   */
  /**
   * Configuración Global «Configuración SML»: el voltaje de alarma de batería de
   * los SML, así que sólo tiene sentido si el cliente los tiene (división
   * Residencial).
   */
  public puedeVerConfigSml() {
    return this.puedeVerMedidores();
  }

  /**
   * Configuración Global «Vista Personalizada»: existe para Correctoras y
   * Residencial (`VistasPersonalizadasPorDivision`).
   */
  public puedeVerVistaPersonalizada() {
    return this.divisionesVistaPersonalizada().length > 0;
  }

  public divisionesVistaPersonalizada(): DivisionVistaPersonalizada[] {
    const divisiones: DivisionVistaPersonalizada[] = [];
    if (this.puedeVerCorrectoras()) divisiones.push('Correctoras');
    if (this.puedeVerResidencial()) divisiones.push('Residencial');
    return divisiones;
  }

  /**
   * «Versiones de Firmware» (menú superior): changelog del NUC (Correctoras) y
   * del NSP (Presión). Sin ninguna de las dos divisiones no hay nada que ver.
   */
  public puedeVerVersionesFirmware() {
    return (
      this.esAdminGlobal() &&
      (this.puedeVerCorrectoras() || this.puedeVerUnidadPresion())
    );
  }

  public puedeVerParametrosObis() {
    return (
      HelperService.rolesGlobales(['Administrador']) &&
      this.puedeVerMedidoresElectricos()
    );
  }

  /**
   * Configuración Global «Ciclo de Facturación»: el día de cierre del período de la
   * división Residencial Agua.
   *
   * Mismo patrón que `puedeVerParametrosObis`: feature del cliente (tener medidores de
   * agua) + rol 'Administrador', que es lo que exige
   * `PUT /clientes/cicloFacturacion` en gas-api-cliente. Sin el gate del rol el tab se
   * vería y el guardado se comería un 403.
   */
  public puedeVerCicloFacturacion() {
    return (
      HelperService.rolesGlobales(['Administrador']) &&
      this.puedeVerResidencialAgua()
    );
  }

  // Cobertura LoRaWAN: combina el flag del cliente
  // (config.moduloCoberturaLorawan.activo) con un rol global de lectura.
  // Mismo patrón que los demás puedeVer*: feature del cliente + rol.
  public puedeVerCoberturaLorawan() {
    const user = LoginService.getUsuario();
    if (!user.cliente?.config?.moduloCoberturaLorawan?.activo) {
      return false;
    }
    // Roles Globales
    if (
      HelperService.rolesGlobales(['Administrador', 'Usuario', 'Visualizar'])
    ) {
      return true;
    }
    return false;
  }

  // Métricas de gateways (RSSI, SNR, distancia) dentro de la cobertura LoRaWAN.
  // Suma al gate base (activo + rol) el flag por cliente
  // config.moduloCoberturaLorawan.verMetricas, que el admin persiste para
  // habilitar el proxy de métricas a ChirpStack. Si está apagado, la vista de
  // cobertura sigue disponible pero sin las columnas/stat-cards de métricas.
  public puedeVerMetricasCoberturaLorawan() {
    if (!this.puedeVerCoberturaLorawan()) {
      return false;
    }
    const user = LoginService.getUsuario();
    return !!user.cliente?.config?.moduloCoberturaLorawan?.verMetricas;
  }

  // Sección "Clima" (vistas resumen por UN / Centro Operativo / Localidad).
  // Módulo activable por cliente y por usuario: combina config.moduloClima.activo
  // y usuario.modulos.clima con el mismo permiso que la vista de puntos de
  // medición (de donde sale el dato agregado). Si falta un flag, la sección no
  // aparece en el menú y la ruta /clima redirige (ModuloClimaGuard). El flag del
  // usuario es temporal y sólo lo mira el front.
  public puedeVerClima() {
    const user = LoginService.getUsuario();
    if (!user.cliente?.config?.moduloClima?.activo || !user.modulos?.clima) {
      return false;
    }
    return this.puedeVerPuntosDeMedicion();
  }

  // Asistente de ayuda: el cliente lo tiene habilitado (config.usaLlm, desde el admin) y el
  // usuario tiene la tilde "Usa el asistente". Sólo muestra u oculta; la API vuelve a chequearlo.
  public puedeUsarAsistente() {
    const user = LoginService.getUsuario();
    return !!user.cliente?.config?.usaLlm && !!user.usaLlm;
  }

  // Consumo de tokens del asistente: el admin global de un cliente que lo tiene, use él o no el asistente.
  // La API vuelve a chequear las dos cosas.
  public puedeVerConsumoAsistente() {
    return (
      !!LoginService.getUsuario().cliente?.config?.usaLlm &&
      this.esAdminGlobal()
    );
  }

  // El asistente consulta datos (MCP horacito) para los mismos roles que pueden ver puntos: el resto
  // sólo tiene la ayuda. Para los ejemplos del chat; el recorte real lo hacen el MCP y la API.
  public asistenteConsultaDatos(): boolean {
    const roles = ['Administrador', 'Usuario', 'Visualizar'];
    return (LoginService.getUsuario()?.permisos ?? []).some((p) =>
      roles.includes(p.rol!),
    );
  }

  // Con qué nombre e imagen se presenta el asistente en este cliente (cliente.config.asistente,
  // desde el admin). "Horacito" y la H son los nuestros.
  public asistente(): { nombre: string; imagen: string } {
    const a = LoginService.getUsuario()?.cliente?.config?.asistente;
    return {
      nombre: a?.nombre?.trim() || 'Horacito',
      imagen: this.imagenDelAsistente(a?.imagen?.trim()),
    };
  }

  private imagenAsistente?: { ruta: string; url: string };

  // La imagen subida desde el admin es una ruta del bucket privado (asistentes/…): se pide a la API con
  // la sesión, una vez, y se muestra como blob. Mientras llega, o si falla, la H. Una URL (cargada antes
  // de poder subir la imagen) se usa tal cual.
  private imagenDelAsistente(valor?: string): string {
    const h = 'assets/images/logo-h.png';
    if (!valor) return h;
    if (/^https?:\/\//i.test(valor)) return valor;
    if (this.imagenAsistente?.ruta !== valor) {
      const actual = { ruta: valor, url: h };
      this.imagenAsistente = actual;
      // no-store: la URL es la misma para todos los clientes y el navegador devolvía la imagen de otro.
      fetch(`${api}/asistente/imagen`, {
        headers: { Authorization: `Bearer ${LoginService.getToken()}` },
        cache: 'no-store',
      })
        .then((r) => (r.ok ? r.blob() : Promise.reject(r.status)))
        .then((b) => (actual.url = URL.createObjectURL(b)))
        .catch(() => undefined);
    }
    return this.imagenAsistente.url;
  }

  // Integración on-premise (enlace + adaptador OPC-UA): pantalla e indicador de la barra.
  // Sólo el administrador global, y sólo con config.onPremise.modo === 'activo'.
  public puedeVerIntegracionOnPremise() {
    const user = LoginService.getUsuario();
    return (
      user.cliente?.config?.onPremise?.modo === 'activo' && this.esAdminGlobal()
    );
  }

  // Sección Red: sistemas (la red del GIS y los puntos de cada subsistema),
  // puntos (la clasificación del padrón) y conexiones (la topología). Módulo
  // activable por cliente (config.moduloRed.activo) y por usuario (modulos.red),
  // con el permiso de puntos de medición. Sin excepción para el administrador
  // global; la API exige los dos flags en /red, en la clasificación y en la
  // asignación de sistema. Apagado, el ítem no aparece en el menú y /red
  // redirige (ModuloRedGuard).
  //
  // config.moduloClasificacion ya no muestra nada: es el interruptor del
  // clasificador automático de gas-cron. config.moduloTopologia tampoco: las
  // conexiones son parte de Red y van con su flag.
  public puedeVerRed() {
    const user = LoginService.getUsuario();
    if (!user.cliente?.config?.moduloRed?.activo || !user.modulos?.red) {
      return false;
    }
    return this.puedeVerPuntosDeMedicion();
  }

  // Navegación configurable de la vista de operaciones. Módulo activable por
  // cliente (config.moduloNavegacion.activo) y por usuario (modulos.navegacion),
  // combinado con el permiso de SCADA. Sin excepción para el administrador
  // global. El árbol que se muestra lo recorta el backend, que también exige los
  // dos flags; esto sólo decide si aparece la sección.
  public puedeVerNavegacion() {
    const user = LoginService.getUsuario();
    if (
      !user.cliente?.config?.moduloNavegacion?.activo ||
      !user.modulos?.navegacion
    ) {
      return false;
    }
    return this.puedeVerScadaMediciones() || this.puedeVerScadaUnifilares();
  }

  // Linepack, dentro de la vista de operaciones: además de Operación, el flag
  // del cliente config.moduloLinepack.activo y el del usuario modulos.linepack.
  // El backend exige lo mismo en /linepack/red.
  public puedeVerLinepack() {
    const user = LoginService.getUsuario();
    return (
      this.puedeVerNavegacion() &&
      !!user.cliente?.config?.moduloLinepack?.activo &&
      !!user.modulos?.linepack
    );
  }

  // Editar la navegación: el editor muestra el árbol entero del cliente, así
  // que sólo un administrador global (el backend exige lo mismo).
  public puedeEditarNavegacion() {
    return this.puedeVerNavegacion() && this.esAdminGlobal();
  }

  // Editar UN/CO de un dispositivo aunque esté asignado a una entidad
  // intermedia. Combina el flag del cliente
  // (config.permitirEditarUnNcoAsignado) con rol Admin Global. La localidad no
  // depende de este flag. El backend valida lo mismo al persistir.
  public permiteEditarUnNcoAsignado() {
    const user = LoginService.getUsuario();
    if (!user.cliente?.config?.permitirEditarUnNcoAsignado) {
      return false;
    }
    return this.esAdminGlobal();
  }

  // Gestión de cuentas de facturación (integración externa, p. ej. Manantial
  // de AYSAM). Capability de solo-visualización: el backend la auto-setea en
  // config.gestionCuentas al configurar la integración; no hay toggle en admin.
  // Sin requisito de rol: si el cliente la tiene, se muestran los datos de
  // cuenta en el punto de medición.
  public gestionCuentas() {
    const user = LoginService.getUsuario();
    return !!user.cliente?.config?.gestionCuentas;
  }

  public puedeImportarCromatografias() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Laboratorista'])) {
      return true;
    }

    return false;
  }
  public puedeCrearCromatografias() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Croma'])) {
      return true;
    }

    // Roles por unidad de negocio
    if (HelperService.rolesEnCualquierUnidadNegocio(['Croma'])) {
      return true;
    }

    // Roles por centro operativo
    if (HelperService.rolesEnCualquierCentroOperativo(['Croma'])) {
      return true;
    }

    return false;
  }
  public puedeEditarCromatografia() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Laboratorista'])) {
      return true;
    }
    return false;
  }
  public puedeEliminarCromatografia() {
    // Roles Globales
    if (HelperService.rolesGlobales(['Laboratorista'])) {
      return true;
    }
    return false;
  }

  //////////////////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////// Usuarios ////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////////////////

  // Notificaciones
  public notifError(error: any): void {
    console.error(error);
    // La sesión se cerró y el login ya dice por qué (LoginService.refreshToken).
    if (error?.sesionCerrada) return;
    const mensaje = parsearMensajeError(error);
    console.error(mensaje);
    this.snackBar.open(mensaje, 'Cerrar', { panelClass: ['red-snackbar'] });
  }

  /**
   * Traduce un error de la API a un mensaje entendible por el usuario (misma
   * lógica que `notifError`) sin mostrarlo. Útil cuando el error se muestra en
   * un campo del template en vez de en el snackbar.
   */
  public parsearError(error: any): string {
    return parsearMensajeError(error);
  }
  public notifSuccess(mensaje: string): void {
    console.log(mensaje);
    this.snackBar.open(mensaje, 'Cerrar', {
      duration: 3000,
      panelClass: ['green-snackbar'],
    });
  }
  public notifWarn(mensaje: string): void {
    console.log(mensaje);
    this.snackBar.open(mensaje, 'Cerrar', { panelClass: ['yellow-snackbar'] });
  }

  public puedeEditarMantenimiento(data?: IMantenimiento) {
    if (!data) {
      return false;
    }

    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }

    return false;
  }
  public puedeEliminarMantenimiento(data: IMantenimiento) {
    if (!data) {
      return false;
    }

    // Roles Globales
    if (HelperService.rolesGlobales(['Administrador'])) {
      return true;
    }

    return false;
  }

  // Dispositivos

  public bateriaDispositivoNuc(dispositivo?: IDispositivo): string {
    const config = dispositivo?.config as IConfigDispositivoNUC4G & {
      voltajeBateria?: number;
      firmwareNuc?: string;
    };
    const firmware = config?.firmwareNuc;
    const bateria = config?.voltajeBateria;

    if (bateria === undefined || bateria === null) {
      return 'Sin Reportar';
    }

    if (firmware === 'v18') {
      return '3.61v';
    }

    return `${bateria.toFixed(2)}v`;
  }

  // Punto de medicion

  public estadoPuntoMedicion(punto: IPuntoMedicion) {
    switch (punto.division) {
      case 'Correctoras': {
        return this.estadoCorrectora(punto.correctora);
      }
      case 'Presión': {
        return this.estadoUnidadPresion(punto.unidadPresion);
      }
      default: {
        return 'Ok';
      }
    }
  }

  // Correctoras

  public getUltimaComunicacion(correctora?: ICorrectora): string {
    const ultimoRegistro = correctora?.ultimoRegistro;
    const ultimaAlerta = correctora?.ultimaAlerta;

    const fechaUltimoRegistro = ultimoRegistro?.fechaCreacion;
    const fechaUltimaAlerta = ultimaAlerta?.fechaCreacion;

    if (fechaUltimoRegistro && fechaUltimaAlerta) {
      return fechaUltimoRegistro > fechaUltimaAlerta
        ? fechaUltimoRegistro
        : fechaUltimaAlerta;
    }

    if (fechaUltimoRegistro) {
      return fechaUltimoRegistro;
    }

    if (fechaUltimaAlerta) {
      return fechaUltimaAlerta;
    }

    return '';
  }

  private getFrecReporteNUC(config: IConfigDispositivoNUC4G) {
    const frec = config?.modoOperacion;
    switch (frec) {
      case 'REG8_8HORAS':
        return 8;
      case 'REG1_DIARIO':
        return 24;
      case 'REG24_DIARIO':
        return 24;
      default:
        return 24;
    }
  }

  public estadoCorrectora(correctora?: ICorrectora) {
    if (!correctora?.dispositivo) return 'Sin Asignar';

    if (correctora?.estadoActual === 'En Mantenimiento') {
      return 'En Mantenimiento';
    }

    if (correctora?.estadoActual === 'Resolver') {
      return 'Resolver';
    }

    const config = correctora?.dispositivo?.config as IConfigDispositivoNUC4G;
    const frecComunicacionHs = this.getFrecReporteNUC(config);
    // const timestampMaximo = frecComunicacionHs * 60 * 60 * 1000;
    const fechaLimite = this.fechaHaceXHoras(frecComunicacionHs);

    const f1 = correctora?.ultimoRegistro?.fechaCreacion
      ? new Date(correctora?.ultimoRegistro.fechaCreacion)
      : undefined;
    const f2 = correctora?.ultimoRegistro?.timestamp
      ? new Date(correctora?.ultimoRegistro.timestamp)
      : undefined;

    const ultimoReporte = this.fechaMasReciente(f1, f2);
    const ultimaAlerta = correctora?.ultimaAlerta?.fechaCreacion
      ? new Date(correctora?.ultimaAlerta.fechaCreacion)
      : null;

    // Hay ultima alerta y es mas reciente que la fecha limite
    if (ultimaAlerta && ultimaAlerta > fechaLimite) {
      // No hay ultimo reporte o la ultima alerta es mas reciente que el ultimo reporte
      if (!ultimoReporte || ultimaAlerta > ultimoReporte) {
        return 'Alerta';
      }
    }

    // No hay ultimo reporte o la fecha limite es mas reciente que el ultimo reporte
    if (!ultimoReporte || fechaLimite > ultimoReporte) {
      return 'Sin reportar';
    }

    return 'Ok';
  }

  public bateriaCorrectora(correctora?: ICorrectora): string {
    const bateria = correctora?.bateria;

    if (bateria || bateria === 0) {
      const modelo = correctora?.modelo;
      if (
        modelo === 'Mercury' ||
        modelo === 'Proser' ||
        modelo === 'Dresser2' ||
        modelo === 'AmericanMeter'
      ) {
        const calculada = bateria / 100;
        return `${calculada}v`;
      }
      if (modelo === 'Minicor' || modelo === 'Corus') {
        return `${bateria} días`;
      }
    }
    return 'Sin Reportar';
  }

  // Unidad Presion

  private getFrecReporteNSP(config: IConfigDispositivoNSP4G) {
    const frec = config?.modoOperacion;
    switch (frec) {
      case 'REG1_1HORA':
        return 1;
      case 'REG2_2HORAS':
        return 2;
      case 'REG3_3HORAS':
        return 3;
      case 'REG4_4HORAS':
        return 4;
      case 'REG6_6HORAS':
        return 6;
      case 'REG8_8HORAS':
        return 8;
      case 'REG12_12HORAS':
        return 12;
      case 'REG1_DIARIO':
        return 24;
      case 'REG24_DIARIO':
        return 24;
      default:
        return 24;
    }
  }

  public estadoUnidadPresion(unidadPresion?: IUnidadPresion) {
    if (!unidadPresion?.dispositivo) return 'Sin Asignar';

    if (unidadPresion.estadoActual === 'En Mantenimiento') {
      return 'En Mantenimiento';
    }

    if (unidadPresion.estadoActual === 'Resolver') {
      return 'Resolver';
    }

    const config = unidadPresion.dispositivo?.config as IConfigDispositivoNSP4G;
    // const valores = unidadPresion.ultimoRegistro?.valores as IReporteNSP;
    const frecComunicacionHs = this.getFrecReporteNSP(config);

    const fechaLimite = this.fechaHaceXHoras(frecComunicacionHs);

    const ultimoReporte = unidadPresion.ultimoRegistro?.fechaCreacion
      ? new Date(unidadPresion.ultimoRegistro.fechaCreacion)
      : null;
    const ultimaAlerta = unidadPresion.ultimaAlerta?.fechaCreacion
      ? new Date(unidadPresion.ultimaAlerta.fechaCreacion)
      : null;

    // Hay ultima alerta y es mas reciente que la fecha limite
    if (ultimaAlerta && ultimaAlerta > fechaLimite) {
      // No hay ultimo reporte o la ultima alerta es mas reciente que el ultimo reporte
      if (!ultimoReporte || ultimaAlerta > ultimoReporte) {
        return 'Alerta';
      }
    }

    // No hay ultimo reporte o la fecha limite es mas reciente que el ultimo reporte
    if (!ultimoReporte || fechaLimite > ultimoReporte) {
      return 'Sin reportar';
    }

    return 'Ok';
  }

  public bateriaUnidadPresionVeribox(bateria?: number): string {
    if (bateria) {
      return (bateria / 10).toFixed(2) + 'V.';
    }

    return 'Sin Reportar';
  }

  public bateriaUnidadPresionNSP(bateria?: number): string {
    if (bateria) {
      return bateria.toFixed(2) + 'V.';
    }

    return 'Sin Reportar';
  }

  // Medidores

  public estadoMedidor(medidor: IMedidorResidencial) {
    const ayer = this.fechaAyer();
    const fechaUltimoReporte = medidor.dispositivo?.fechaUltimaComunicacion;
    if (!fechaUltimoReporte || ayer > new Date(fechaUltimoReporte)) {
      return 'Sin reportar';
    }
    return 'Ok';
  }

  public estadoMedidorDeAgua(medidor: IMedidorResidencial) {
    const ayer = this.fechaAyer();
    const fechaUltimoReporte = medidor.dispositivo?.fechaUltimaComunicacion;
    if (!fechaUltimoReporte || ayer > new Date(fechaUltimoReporte)) {
      return 'Sin reportar';
    }
    return 'Ok';
  }

  // Mapa
  public static async getCurrentPosition(): Promise<ICoordenadas> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      const ubicacionBase: ICoordenadas = {
        lat: -35.5836812,
        lng: -58.0128784,
      };
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const ubicacion: ICoordenadas = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            resolve(ubicacion);
          },
          () => {
            console.error('Ubicacion no aceptada');
            resolve(ubicacionBase);
          },
          // Sin timeout el default es Infinity: si el equipo no resuelve la
          // ubicación (permiso sin responder, sin GPS, fix lento) la promesa
          // nunca resuelve y bloquea a quien la awaitea. Cae al fallback a los 5s.
          { timeout: 5000, maximumAge: 300000 },
        );
      } else {
        resolve(ubicacionBase);
      }
    });
  }

  // Fechas
  private fechaAyer() {
    const fecha = new Date();
    fecha.setDate(fecha.getDate() - 1);
    return fecha;
  }

  private fechaHaceXHoras(horas: number) {
    const fecha = new Date();
    fecha.setHours(fecha.getHours() - horas);
    return fecha;
  }

  public fechaHaceXDias(dias = 1): Date {
    const fecha = new Date();
    fecha.setDate(fecha.getDate() - dias);
    return fecha;
  }

  public fechaHaceXAnios(anios = 1): Date {
    const fecha = new Date();
    fecha.setFullYear(fecha.getFullYear() - anios);
    return fecha;
  }

  private fechaMasReciente(f1?: Date, f2?: Date): Date | null {
    if (f1 && f2) {
      return f1 > f2 ? f1 : f2;
    }
    if (f1) {
      return f1;
    }
    if (f2) {
      return f2;
    }
    return null;
  }

  public fechaHaceUnAnio(): Date {
    const fecha = new Date();
    fecha.setFullYear(fecha.getFullYear() - 1);
    return fecha;
  }
  // Estados

  // El ícono de cada estado lo resuelve IconosEstadoService: sale del catálogo,
  // respetando la reasignación por cliente (config.iconosEstado). Antes era un
  // switch con los 9 assets hardcodeados. El tooltip sigue siendo el nombre del
  // estado — es el dato que el usuario necesita, no el del ícono.
  public estadoIconoTabla(estado?: IEstado): IRowIcon {
    return {
      icon: this.iconosEstado.asset(estado),
      tooltip: estado ?? 'Sin Estado',
      tipo: 'img',
    };
  }

  public iconEstadoMedidorAgua(c: IMedidorResidencialAgua) {
    const estado = c.estadoActual;
    return this.estadoIconoTabla(estado);
  }

  public iconEstadoCorrectora(c: ICorrectora) {
    const estado = c.estadoActual;
    return this.estadoIconoTabla(estado);
  }

  public iconEstadoMedidor(c: IMedidorResidencial) {
    const estado = c.estadoActual;
    return this.estadoIconoTabla(estado);
  }

  public iconEstadoUnidadPresion(p: IUnidadPresion) {
    const estado = p.estadoActual;
    return this.estadoIconoTabla(estado);
  }

  // En 'Alerta' el tooltip lista los tipos activos (tiposAlertaActivos,
  // denormalizado en el punto) en vez del nombre genérico del estado.
  public iconEstadoPuntoMedicion(p: IPuntoMedicion) {
    const estado = p.estado;
    const icono = this.estadoIconoTabla(estado);
    const tipos = [...new Set(p.tiposAlertaActivos ?? [])];
    if (estado === 'Alerta' && tipos.length) {
      icono.tooltip = `Alerta: ${tipos.join(', ')}`;
    }
    return icono;
  }

  static parseFecha(fecha?: string): string {
    if (fecha) {
      return `<div>${new Date(fecha).toLocaleDateString('es-AR')} - ${new Date(
        fecha,
      ).toLocaleTimeString('es-AR', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })}</div>`;
    } else {
      return `<div class="no-info">Sin reportar</div>`;
    }
  }

  // Fechas

  static inicioMesAnteriorALaHora(hora: number) {
    const d = new Date();
    d.setUTCMonth(d.getUTCMonth() - 1);
    d.setUTCDate(1);
    d.setUTCHours(hora + 3, 0, 0, 0);
    return d.toISOString();
  }

  static inicioMesActualALaHora(hora: number) {
    const d = new Date();
    d.setUTCDate(1);
    d.setUTCHours(hora + 3, 0, 0, 0);
    return d.toISOString();
  }

  static fechaHaceDiasALaHora(dias: number, hora: number) {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - dias);
    d.setUTCHours(hora + 3, 0, 0, 0);
    return d.toISOString();
  }

  static mesAnteriorALasSeis(fecha: Date) {
    const fechaAux = new Date(fecha);
    fechaAux.setHours(6, 0, 0, 0);
    fechaAux.setMonth(fecha.getMonth() - 1);
    return fechaAux;
  }

  static mesAnteriorALasSiete(fecha: Date) {
    const fechaAux = new Date(fecha);
    fechaAux.setHours(7, 0, 0, 0);
    fechaAux.setMonth(fecha.getMonth() - 1);
    return fechaAux;
  }

  static dateAtSixHours(fecha: Date) {
    const fechaAux = new Date(fecha);
    fechaAux.setHours(6, 0, 0, 0);
    return fechaAux;
  }

  static dateAtSevenHours(fecha: Date) {
    const fechaAux = new Date(fecha);
    fechaAux.setHours(7, 0, 0, 0);
    return fechaAux;
  }

  static fechaGas(dias: number): string {
    const fecha = new Date();
    fecha.setHours(6, 0, 0, 0);
    fecha.setDate(fecha.getDate() - dias);
    return fecha.toISOString();
  }

  static mesAnterior(fecha: Date) {
    const fechaAux = new Date(fecha);
    fechaAux.setMonth(fecha.getMonth() - 1);
    return fechaAux;
  }

  static fechaAnterior(dias: number) {
    const fecha = new Date();
    fecha.setDate(fecha.getDate() - dias);
    return fecha;
  }

  static tomorrow(fecha: Date) {
    const fechaAux = new Date(fecha);
    fechaAux.setDate(fecha.getDate() + 1);
    return fechaAux;
  }

  static roundTo(n: number, digits: number) {
    let negative = false;
    if (digits === undefined) {
      digits = 0;
    }
    if (n < 0) {
      negative = true;
      n = n * -1;
    }
    const multiplicator = Math.pow(10, digits);
    n = parseFloat((n * multiplicator).toFixed(11));
    n = +(Math.round(n) / multiplicator).toFixed(digits);
    if (negative) {
      n = +(n * -1).toFixed(digits);
    }
    return n;
  }

  // Presets de Rango de fechas

  static presetCorrectoras = [
    {
      label: 'Mes Anterior',
      from: HelperService.inicioMesAnteriorALaHora(7),
      to: HelperService.inicioMesActualALaHora(6),
    },
    {
      label: 'Últimas 24 horas',
      from: HelperService.fechaHaceDiasALaHora(1, 7),
      to: HelperService.fechaHaceDiasALaHora(0, 6),
    },
    {
      label: 'Últimas 48 horas',
      from: HelperService.fechaHaceDiasALaHora(2, 7),
      to: HelperService.fechaHaceDiasALaHora(0, 6),
    },
    {
      label: 'Últimos 7 días',
      from: HelperService.fechaHaceDiasALaHora(7, 7),
      to: HelperService.fechaHaceDiasALaHora(0, 6),
    },
    {
      label: 'Últimos 30 días',
      from: HelperService.fechaHaceDiasALaHora(30, 7),
      to: HelperService.fechaHaceDiasALaHora(0, 6),
    },
  ];

  static presetUnidadesPresion = [
    {
      label: 'Mes Anterior',
      from: HelperService.inicioMesAnteriorALaHora(7),
      to: HelperService.inicioMesActualALaHora(6),
    },
    {
      label: 'Últimas 24 horas',
      from: HelperService.fechaHaceDiasALaHora(1, 7),
      to: HelperService.fechaHaceDiasALaHora(-1, 6),
    },
    {
      label: 'Últimas 48 horas',
      from: HelperService.fechaHaceDiasALaHora(2, 7),
      to: HelperService.fechaHaceDiasALaHora(-1, 6),
    },
    {
      label: 'Últimos 7 días',
      from: HelperService.fechaHaceDiasALaHora(7, 7),
      to: HelperService.fechaHaceDiasALaHora(-1, 6),
    },
    {
      label: 'Últimos 30 días',
      from: HelperService.fechaHaceDiasALaHora(30, 7),
      to: HelperService.fechaHaceDiasALaHora(-1, 6),
    },
  ];

  static presetResidencial = [
    {
      label: 'Mes Anterior',
      from: HelperService.inicioMesAnteriorALaHora(7),
      to: HelperService.inicioMesActualALaHora(6),
    },
    {
      label: 'Últimas 24 horas',
      from: HelperService.fechaHaceDiasALaHora(1, 7),
      to: HelperService.fechaHaceDiasALaHora(-1, 6),
    },
    {
      label: 'Últimas 48 horas',
      from: HelperService.fechaHaceDiasALaHora(2, 7),
      to: HelperService.fechaHaceDiasALaHora(-1, 6),
    },
    {
      label: 'Últimos 7 días',
      from: HelperService.fechaHaceDiasALaHora(7, 7),
      to: HelperService.fechaHaceDiasALaHora(-1, 6),
    },
    {
      label: 'Últimos 30 días',
      from: HelperService.fechaHaceDiasALaHora(30, 7),
      to: HelperService.fechaHaceDiasALaHora(-1, 6),
    },
  ];

  // Ciclo de facturación (día de cierre)

  /**
   * El día de cierre configurado para la división, o `undefined` si la división no tiene
   * ciclo de facturación.
   *
   * Se lee del cliente cacheado en el login (no pega a la API): es el mismo lugar de
   * donde salen los otros gates de config, como `moduloCoberturaLorawan`.
   *
   * ⚠️ El cacheado queda viejo hasta que el usuario recarga: la pantalla de
   * configuración refresca el usuario después de guardar (ver
   * `ConfigCicloFacturacionComponent.guardar`), si no el selector de período no aparece
   * hasta el próximo login.
   *
   * Un valor fuera de 1-28 (config vieja o editada a mano) devuelve `undefined` y apaga
   * la feature, en vez de dejar que el cálculo del período tire.
   */
  static diaCierreFacturacion(division?: Division): number | undefined {
    if (!division) return undefined;
    const user = LoginService.getUsuario();
    const ciclos = user?.cliente?.config?.ciclosFacturacion as
      Record<string, { diaCierre?: number } | undefined> | undefined;
    const diaCierre = ciclos?.[division]?.diaCierre;
    return esDiaCierreValido(diaCierre) ? diaCierre : undefined;
  }

  public diaCierreFacturacion(division?: Division): number | undefined {
    return HelperService.diaCierreFacturacion(division);
  }

  /**
   * Presets de rango, uno por período de facturación, del más reciente al más viejo.
   * Vacío si la división no tiene día de cierre configurado.
   *
   * **Método y no `static` field como los `presetXxx` de arriba**: esos se evalúan una
   * sola vez al cargar la clase, y estos dependen de la config del cliente (que no está
   * disponible en ese momento) y del mes en curso.
   *
   * El `Date` se arma con el constructor por partes, así que los bordes caen a las 00:00
   * **locales del navegador** — que es la definición del corte (ver
   * `CicloFacturacionSchema` en modelos). El fin del período es exclusive, así que el
   * `to` del preset se corre 1 ms para atrás: el rango que consumen las vistas y el
   * export es cerrado (`$lte`) y sin ese ajuste el último día del período se solaparía
   * con el primero del siguiente.
   */
  static presetsPeriodosFacturacion(
    division?: Division,
    cantidad = 12,
  ): IRangePreset[] {
    const diaCierre = HelperService.diaCierreFacturacion(division);
    if (!diaCierre) return [];

    const hoy = new Date();
    const enCurso = periodoDeFecha(diaCierre, {
      anio: hoy.getFullYear(),
      mes: hoy.getMonth() + 1,
      dia: hoy.getDate(),
    });

    return periodosFacturacion(
      diaCierre,
      enCurso.anio,
      enCurso.mes,
      cantidad,
    ).map((p) => {
      const desde = new Date(p.inicio.anio, p.inicio.mes - 1, p.inicio.dia);
      const hasta = new Date(p.fin.anio, p.fin.mes - 1, p.fin.dia);
      hasta.setMilliseconds(hasta.getMilliseconds() - 1);
      return {
        label: `${p.label} (${HelperService.diaMes(desde)} al ${HelperService.diaMes(hasta)})`,
        from: desde.toISOString(),
        to: hasta.toISOString(),
      };
    });
  }

  public presetsPeriodosFacturacion(
    division?: Division,
    cantidad = 12,
  ): IRangePreset[] {
    return HelperService.presetsPeriodosFacturacion(division, cantidad);
  }

  /**
   * El rango del período de facturación en curso, o `undefined` si la división no tiene
   * día de cierre. Es el rango inicial de las vistas de detalle cuando el ciclo está
   * configurado.
   *
   * El `to` cae en el futuro (el período todavía no cerró); no molesta porque el filtro
   * es un `$lte` y no hay datos más adelante.
   */
  static rangoPeriodoEnCurso(
    division?: Division,
  ): { from: string; to: string } | undefined {
    const [enCurso] = HelperService.presetsPeriodosFacturacion(division, 1);
    return enCurso ? { from: enCurso.from, to: enCurso.to } : undefined;
  }

  /** "05/07", para las etiquetas de los períodos. */
  private static diaMes(fecha: Date): string {
    const dia = `0${fecha.getDate()}`.slice(-2);
    const mes = `0${fecha.getMonth() + 1}`.slice(-2);
    return `${dia}/${mes}`;
  }

  public parsearDevEui(d?: string) {
    // Quitar ceros del principio
    if (d) {
      return d.replace(/^0+/, '');
    } else {
      return '';
    }
  }

  static telValido(telefono?: string) {
    const regTel = /^(?!0|15)\d{10}$/;
    return regTel.test(telefono || '');
  }

  // Mapa división → tipos de dispositivo (tipoDispositivo) que le corresponden.
  // Los dispositivos no tienen campo `division`, así que el filtro global por
  // división se traduce a un filtro por tipoDispositivo. "Residencial" incluye
  // SML y WRC (unificados). Las divisiones sin representación física de
  // dispositivo (SCADA, Dispositivo Externo NUC) no se mapean.
  public static readonly TIPOS_DISPOSITIVO_POR_DIVISION: Record<
    string,
    TipoDispositivo[]
  > = {
    Correctoras: ['NUC'],
    Presión: ['VERIBOX MICRO', 'NSP'],
    Residencial: ['SML', 'WRC'],
    'Residencial Agua': [
      'ML107A',
      'ML107GH',
      'EUW300',
      'BOVE',
      'MRA',
      'UWM-NB',
    ],
    'Medidores Eléctricos': ['NME'],
    'Estaciones de Bombeo': ['UC300'],
  };

  public static tiposDispositivoPorDivision(
    division?: string,
  ): TipoDispositivo[] {
    if (!division) return [];
    return HelperService.TIPOS_DISPOSITIVO_POR_DIVISION[division] || [];
  }

  public static formatearTipoDispositivo(tipo?: TipoDispositivo): string {
    if (!tipo) return '';
    // WRC y SML se unifican de cara al usuario bajo el label "SML".
    if (tipo === 'WRC') return 'SML';
    return tipo;
  }

  // Expande el filtro por `tipoDispositivo` para que seleccionar "SML" matchee
  // también los 'WRC' (unificados en la UI). Recibe y devuelve el filtro ya
  // serializado (JSON), tal cual lo maneja la tabla. No muta el original.
  public static expandirFiltroTipoSml(filterStr?: string): string | undefined {
    if (!filterStr) return filterStr;
    try {
      const filter = JSON.parse(filterStr);
      const expandir = (val: any): any => {
        if (val && typeof val === 'object' && Array.isArray(val.$in)) {
          if (val.$in.includes('SML') && !val.$in.includes('WRC')) {
            return { ...val, $in: [...val.$in, 'WRC'] };
          }
          return val;
        }
        if (val === 'SML') return { $in: ['SML', 'WRC'] };
        return val;
      };
      if (Array.isArray(filter.$and)) {
        filter.$and = filter.$and.map((cond: any) =>
          cond && cond.tipoDispositivo !== undefined
            ? { ...cond, tipoDispositivo: expandir(cond.tipoDispositivo) }
            : cond,
        );
      }
      if (filter.tipoDispositivo !== undefined) {
        filter.tipoDispositivo = expandir(filter.tipoDispositivo);
      }
      return JSON.stringify(filter);
    } catch {
      return filterStr;
    }
  }

  // Medidores Redidenciales
  public calcularConsumoSML4GValor(valores: IReporteWRC, inicial = 0): number {
    if (!valores) return 0;
    const pulseConstant = valores.meter_info?.pulse_constant || 0;
    const meter_reading = valores.meter_info?.meter_reading || 0;
    if (!pulseConstant) return 0;
    switch (pulseConstant) {
      case 0 || 1:
        return +(meter_reading / 10000 + inicial).toFixed(2);
      case 2:
        return +((meter_reading * 10) / 10000 + inicial).toFixed(2);
      case 3:
        return +((meter_reading * 100) / 10000 + inicial).toFixed(2);
      case 4:
        return +((meter_reading * 1000) / 10000 + inicial).toFixed(2);
      case 5:
        return +((meter_reading * 0.5) / 10000 + inicial).toFixed(2);
      case 6:
        return +((meter_reading * 5) / 10000 + inicial).toFixed(2);
      case 7:
        return +((meter_reading * 10000) / 10000 + inicial).toFixed(2);
      default:
        return 0;
    }
  }

  public generateColorsFromString(input: string): {
    backgroundColor: string;
    textColor: string;
  } {
    // Hash the input string to generate a number
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      hash = input.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Convert hash to a color in HEX format
    const backgroundColor = `#${((hash >> 24) & 0xff)
      .toString(16)
      .padStart(2, '0')}${((hash >> 16) & 0xff)
      .toString(16)
      .padStart(2, '0')}${((hash >> 8) & 0xff).toString(16).padStart(2, '0')}`;

    // Function to calculate brightness of a color
    const getBrightness = (hexColor: string): number => {
      const r = parseInt(hexColor.slice(1, 3), 16);
      const g = parseInt(hexColor.slice(3, 5), 16);
      const b = parseInt(hexColor.slice(5, 7), 16);
      return (r * 299 + g * 587 + b * 114) / 1000;
    };

    // Decide the text color based on brightness
    const brightness = getBrightness(backgroundColor);
    const textColor = brightness > 125 ? '#000000' : '#FFFFFF';

    return { backgroundColor, textColor };
  }

  private isNumber(value: any): boolean {
    return typeof value === 'number' && !isNaN(value);
  }

  public margenesIguales(actual?: number, deseado?: number): boolean {
    if (!this.isNumber(actual) || !this.isNumber(deseado)) {
      return false;
    }
    // Si la dif es menor a 0.01, se considera igual
    const dif = Math.abs(actual! - deseado!);
    return dif < 0.01;
  }

  public tipoScadaAUnidad(tipo: TipoScada): string {
    switch (tipo) {
      case 'Presión en bar':
        return 'bar';
      case 'Presión en mbar':
        return 'mbar';
      case 'Temperatura en C':
        return '°C';
      case 'Porcentaje':
        return '%';
      case 'mg sobre m3':
        return 'mg/m3';
      case 'Voltaje en V':
        return 'V';
      case 'Corriente en A':
        return 'A';
      case 'Potencial en mV':
        return 'mV';
      case 'Caudal en m3/h':
        return 'm3/h';
      default:
        return '';
    }
  }

  public formatearFecha(fecha?: Date | string): string {
    if (!fecha) return '';

    const fechaObj = new Date(fecha);
    return fechaObj.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  public consumoAgua(
    reporte: IReporte | null | undefined,
    tipoDispositivo?: TipoDispositivo,
  ): number | null {
    if (!reporte) return null;
    switch (tipoDispositivo) {
      case 'BOVE': {
        const valores = reporte.valores as IReporteTotalizadorBove;
        return HelperService.isDefined(valores?.consumo)
          ? valores.consumo!
          : null;
      }
      case 'EUW300': {
        const valores = reporte.valores as IReporteDiarioEUW300;
        return HelperService.isDefined(valores?.consumo)
          ? valores.consumo!
          : null;
      }
      case 'WRC': {
        const valores = reporte.valores as IReporteWRC;
        return HelperService.isDefined(valores?.consumo)
          ? valores.consumo!
          : null;
      }
      case 'ML107GH': {
        const valores = reporte.valores as IReporteML107GH;
        return HelperService.isDefined(valores?.flujoPositivo)
          ? (valores.flujoPositivo || 0) - (valores.flujoNegativo || 0)
          : null;
      }
      case 'SML': {
        const valores = reporte.valores as IReporteSML;
        return HelperService.isDefined(valores?.consumoPositivo)
          ? (valores.consumoPositivo || 0) - (valores.consumoNegativo || 0)
          : null;
      }
      // MRA = dispositivo SML sobre medidor de agua: reporte IReporteSML con
      // el consumo neto en m³ (valores.consumo), igual que ML107A.
      case 'MRA':
      case 'ML107A': {
        const valores = reporte.valores as IReporteSML;
        return HelperService.isDefined(valores?.consumo)
          ? valores.consumo!
          : null;
      }
      // UWM-NB = medidor NB-IoT ultrasónico: volumen total (odómetro) en m³.
      case 'UWM-NB': {
        const valores = reporte.valores as IReporteUWMNB;
        return HelperService.isDefined(valores?.consumo)
          ? valores.consumo!
          : null;
      }
      default:
        return null;
    }
  }

  public consumoAguaDisplay(
    reporte: IReporte | null | undefined,
    tipoDispositivo?: TipoDispositivo,
  ): string | null {
    const consumo = this.consumoAgua(reporte, tipoDispositivo);
    if (consumo === null || !reporte) {
      return null;
    }

    let unidad = 'm³';
    if (tipoDispositivo === 'EUW300') {
      // Para EUW300, usar unidadFlujoAcumulado si existe
      const euwValores = reporte.valores as
        IReporteDiarioEUW300 | IReporteHorarioEUW300;
      unidad = euwValores.unidadFlujoAcumulado || 'm³';
    }

    return `${consumo.toFixed(2)} ${unidad}`;
  }

  /**
   * Consumo del PERÍODO que mide un reporte residencial (gas o agua).
   *
   * ⚠️ **Las dos familias usan campos disjuntos**, verificado en producción:
   * el WRC escribe `consumoInstantaneo` (los diffs del bloque dense) y nunca
   * `consumoParcial`; SML Hac/SindCon, MRA y ML107A escriben `consumoParcial`
   * (calculado en el backend) y nunca `consumoInstantaneo`. Leer uno solo deja la
   * columna vacía para media flota — le pasaba a los 547 SML, 5 MRA y 1075 ML107A.
   *
   * Es el dato PRIMITIVO de la serie: todo consumo de período se suma de acá y
   * nunca se resta de dos acumulados (ver PLAN-ACUMULADO-RESIDENCIAL-TRAZABILIDAD,
   * invariante 1). El parcial lo mide el equipo, así que es inmune a los cambios de
   * ancla del medidor y a los recambios de dispositivo.
   */
  public parcialResidencial(
    reporte: IReporte | null | undefined,
  ): number | null {
    const valores = reporte?.valores as
      { consumoParcial?: number; consumoInstantaneo?: number } | undefined;
    if (HelperService.isDefined(valores?.consumoParcial)) {
      return valores!.consumoParcial!;
    }
    if (HelperService.isDefined(valores?.consumoInstantaneo)) {
      return valores!.consumoInstantaneo!;
    }
    return null;
  }

  /** Suma de los parciales de una serie de reportes. Ignora los que no traen. */
  public sumaParcialesResidencial(
    reportes: IReporte[] | null | undefined,
  ): number {
    return (reportes ?? []).reduce((acc, r) => {
      const p = this.parcialResidencial(r);
      return p === null ? acc : acc + p;
    }, 0);
  }

  /**
   * El reporte declara que el acumulado del medidor NO se puede afirmar: el odómetro
   * del equipo quedó por debajo del baseline del vínculo.
   *
   * Es distinto de "no hay dato" y de "esta división no calcula el acumulado del
   * medidor" (EUW300/BOVE). Quien lo muestre tiene que decir cuál de los tres es.
   */
  public acumuladoMedidorIncoherente(
    reporte: IReporte | null | undefined,
  ): boolean {
    const valores = reporte?.valores as
      { baselineIncoherente?: boolean } | undefined;
    return valores?.baselineIncoherente === true;
  }

  // Consumo acumulado del MEDIDOR = consumo del dispositivo + consumoInicial
  // cargado en la plataforma (valores.consumoCorregido). Es el valor a mostrar
  // por defecto en las vistas de punto de medición. Si el reporte no trae
  // consumoCorregido (ej. EUW300, donde no se aplica consumoInicial), cae al
  // consumo del dispositivo.
  public consumoAguaMedidor(
    reporte: IReporte | null | undefined,
    tipoDispositivo?: TipoDispositivo,
  ): number | null {
    const valores = reporte?.valores as
      { consumoCorregido?: number } | undefined;
    if (HelperService.isDefined(valores?.consumoCorregido)) {
      return valores!.consumoCorregido!;
    }
    // ⚠️ El fallback al odómetro del dispositivo es para las divisiones que NO
    // calculan el acumulado del medidor (EUW300, BOVE). Cuando el reporte dice que
    // el acumulado no se puede afirmar, caer al odómetro mostraría un número que no
    // es del medidor y encima parecería válido: ahí no hay valor.
    if (this.acumuladoMedidorIncoherente(reporte)) {
      return null;
    }
    return this.consumoAgua(reporte, tipoDispositivo);
  }

  public consumoAguaMedidorDisplay(
    reporte: IReporte | null | undefined,
    tipoDispositivo?: TipoDispositivo,
  ): string | null {
    const consumo = this.consumoAguaMedidor(reporte, tipoDispositivo);
    if (consumo === null || !reporte) {
      return null;
    }
    let unidad = 'm³';
    if (tipoDispositivo === 'EUW300') {
      const euwValores = reporte.valores as
        IReporteDiarioEUW300 | IReporteHorarioEUW300;
      unidad = euwValores.unidadFlujoAcumulado || 'm³';
    }
    return `${consumo.toFixed(2)} ${unidad}`;
  }

  /**
   * Normaliza un valor de flujo EUW300 a m³.
   *
   * El backend ya aplicó los 2 decimales implícitos Y el factor ×10/×100 de la
   * unidad (`applyDecimalsToFlow` en gas-api-euw300), así que el valor viene en la
   * unidad BASE: litros para `L*`, metros cúbicos para `m³*`. Volver a aplicar el
   * factor lo infla 10× o 100×.
   */
  public static euw300EnM3(
    valor: number,
    unidad?: UnidadFlujoAcumulado,
  ): number {
    // Sin unidad se asume m³ (es lo que reportan los equipos instalados).
    if (!unidad) return valor;
    return unidad.startsWith('L') ? valor / 1000 : valor;
  }

  /**
   * Consumo de agua de una ventana de reportes, en m³.
   *
   * TODOS los dispositivos de agua reportan `valores.consumo` como acumulador
   * (odómetro), EUW300 incluido: ahí `consumo` es un alias de
   * `flujoAcumuladoActual` (ver `IReporteDiarioEUW300` en gas-modelos). Por eso el
   * consumo del período es siempre una diferencia (más reciente − referencia),
   * nunca una suma de las lecturas.
   *
   * `datos` debe venir ordenado por fecha DESC (datos[0] = más reciente) y con la
   * ventana completa (limit 0), si no la diferencia toma un "primer" equivocado.
   * `referencia` es la lectura previa a la ventana (baseline): necesaria cuando el
   * dispositivo reporta poco seguido y la ventana trae un solo reporte, porque ahí
   * la diferencia interna da 0. Ver `consumo-residencial-agua`.
   *
   * Sin baseline (no hay ningún reporte anterior a la ventana) la referencia sale
   * del propio reporte más viejo: su lectura MENOS su consumo del período
   * (`consumoParcial`). Antes se usaba su lectura tal cual, y eso dejaba afuera el
   * consumo de ese primer reporte — en el primero de todos, el del alta del equipo,
   * se perdía todo el acumulado (Unidad 393: card mensual 5.50 en vez de 5.82 m³).
   * Restar el parcial también hace lo correcto cuando el medidor es nuevo pero el
   * dispositivo no (recambio de medidor): ahí el parcial es el del período y no el
   * odómetro entero, así que no se le imputa consumo viejo del equipo.
   * Los reportes sin `consumoParcial` (anteriores a que el backend lo calcule) caen
   * al comportamiento anterior.
   */
  public calcularConsumoAgua(
    datos: IReporte[],
    tipoDispositivo?: TipoDispositivo,
    referencia?: number,
  ): number {
    if (!datos?.length) return 0;

    if (tipoDispositivo === 'EUW300') {
      // Solo los reportes que traen el odómetro (diario/evento). Los horarios no
      // tienen `consumo`; su parcial se contabiliza vía el odómetro del diario.
      const conOdometro = datos.filter((r) => {
        const v = r.valores as IReporteDiarioEUW300;
        return HelperService.isDefined(v?.consumo);
      });
      if (!conOdometro.length) return 0;

      const ultimoV = conOdometro[0].valores as IReporteDiarioEUW300;
      const primerV = conOdometro[conOdometro.length - 1]
        .valores as IReporteDiarioEUW300;
      const unidad = ultimoV.unidadFlujoAcumulado;

      const ultimo = HelperService.euw300EnM3(ultimoV.consumo!, unidad);
      // `consumo` y `consumoParcial` vienen los dos en `unidadFlujoAcumulado`.
      const primer = HelperService.euw300EnM3(primerV.consumo!, unidad);
      const primerParcial = HelperService.isDefined(primerV.consumoParcial)
        ? HelperService.euw300EnM3(primerV.consumoParcial!, unidad)
        : undefined;
      const ref = HelperService.isDefined(referencia)
        ? HelperService.euw300EnM3(referencia!, unidad)
        : primer - (primerParcial ?? 0);

      const consumo = +(ultimo - ref).toFixed(2);
      return consumo >= 0 ? consumo : 0;
    }

    const ultimoV = datos[0].valores as { consumo?: number };
    const primerV = datos[datos.length - 1].valores as {
      consumo?: number;
      consumoParcial?: number;
    };
    const ultimo = ultimoV?.consumo || 0;
    const primer = primerV?.consumo || 0;
    const ref = HelperService.isDefined(referencia)
      ? referencia!
      : primer - (primerV?.consumoParcial ?? 0);
    return +(ultimo - ref).toFixed(2);
  }

  public BateriaAgua(
    reporte: IReporte | null | undefined,
    tipoDispositivo?: TipoDispositivo,
  ): number | null {
    if (!reporte) return null;
    switch (tipoDispositivo) {
      case 'BOVE': {
        const valores = reporte.valores as IReporteTotalizadorBove;
        // bateryCapacity es un byte 0-255; se muestra como % (0-100).
        // Ej: 254 -> round(254/2.55) = 100%.
        return HelperService.isDefined(valores?.bateryCapacity)
          ? Math.round(valores.bateryCapacity! / 2.55)
          : null;
      }
      case 'EUW300': {
        const valores = reporte.valores as IReporteDiarioEUW300;
        return HelperService.isDefined(valores?.nivelBateria)
          ? +valores.nivelBateria!.toFixed(0)
          : null;
      }
      case 'WRC': {
        const valores = reporte.valores as IReporteWRC;
        return HelperService.isDefined(valores?.bateria)
          ? +valores.bateria!.toFixed(2)
          : null;
      }
      case 'ML107GH': {
        const valores = reporte.valores as IReporteML107GH;
        return HelperService.isDefined(valores?.voltajeBateria)
          ? +valores.voltajeBateria!.toFixed(2)
          : null;
      }
      case 'SML': {
        const valores = reporte.valores as IReporteSML;
        return HelperService.isDefined(valores?.bateria)
          ? +valores.bateria!.toFixed(2)
          : null;
      }
      // MRA = dispositivo SML sobre medidor de agua: misma batería que SML.
      case 'MRA':
      case 'ML107A': {
        const valores = reporte.valores as IReporteSML;
        return HelperService.isDefined(valores?.bateria)
          ? +valores.bateria!.toFixed(2)
          : null;
      }
      // UWM-NB reporta batería ya en % (estimado del voltaje de medición).
      case 'UWM-NB': {
        const valores = reporte.valores as IReporteUWMNB;
        return HelperService.isDefined(valores?.bateria)
          ? +valores.bateria!.toFixed(0)
          : null;
      }
      default:
        return null;
    }
  }

  public bateriaAguaDisplay(
    reporte: IReporte | null | undefined,
    tipoDispositivo?: TipoDispositivo,
  ): string | null {
    const bateria = this.BateriaAgua(reporte, tipoDispositivo);
    if (bateria === null) {
      return null;
    }

    let unidad = 'v.';
    if (
      tipoDispositivo === 'EUW300' ||
      tipoDispositivo === 'BOVE' ||
      tipoDispositivo === 'UWM-NB'
    ) {
      unidad = '%';
    }

    // Color según voltaje
    // let colorClass = 'bateria-normal';
    // if (bateria < 3.0) {
    //   colorClass = 'bateria-baja';
    // } else if (bateria < 3.3) {
    //   colorClass = 'bateria-media';
    // }

    return `${bateria} ${unidad}`;
  }
}
